/**
 * Translated <title> and <meta description> per language, per route.
 *
 * Read by prerender.mjs so the metadata is in the HTML a crawler is served,
 * not applied after hydration — that is the whole point of the /es/ path
 * scheme. useSEO reads the same map on client navigation.
 *
 * ── Coverage is deliberately partial ─────────────────────────────────────────
 *
 * A route with no entry for a language falls back to the English copy in
 * seoData.ts. That is a real fallback, not a gap to be papered over: shipping a
 * machine-guessed title in a language nobody on the team reads is worse than
 * shipping an accurate English one, because the error is invisible until a
 * native speaker complains. Adding a language is pure data entry here — no code
 * change — and check-seo-locales.mjs reports the current coverage.
 *
 * Route keys match SEO_BY_ROUTE exactly. The `| TheNameWheel` suffix is added
 * by the caller, as it is for English, so it must not appear here.
 */

export interface LocalisedSEO {
  title: string;
  description: string;
  /**
   * On-page heading. Optional and independent of `title`: the two serve
   * different readers — `title` is written for a search result, `h1` for
   * someone already on the page — so a route can legitimately have one
   * translated and not the other.
   */
  h1?: string;
  /**
   * Lead paragraph shown under the wheel. An array so a translator can break
   * where their language reads naturally rather than being forced to match
   * English sentence boundaries.
   */
  body?: string[];
}

/**
 * A route's copy with every field resolved, plus what actually got translated.
 *
 * The flags are the point. Falling back silently is what produced 542 pages
 * serving English metadata under a language prefix without anything reporting
 * it; a resolver that says which fields are real makes coverage measurable and
 * lets the build refuse to advertise a translation that does not exist.
 */
export interface ResolvedContent {
  title: string;
  description: string;
  h1: string;
  body: string[];
  translated: { title: boolean; description: boolean; h1: boolean; body: boolean };
}

/** True when nothing on the page is in the requested language. */
export function isUntranslated(c: ResolvedContent): boolean {
  return !c.translated.title && !c.translated.description && !c.translated.h1 && !c.translated.body;
}

/**
 * Languages whose pages are allowed into the index and the hreflang cluster.
 *
 * Empty means "English only". Add a code here once its content is genuinely
 * translated — not before.
 *
 * ── Why this gate exists ─────────────────────────────────────────────────────
 *
 * Measured across 27 sampled pages, the localised routes were **4% target
 * script and 96% Latin**: the locale files translate UI chrome (buttons, nav,
 * labels) but the FAQ answers, body copy and marketing sections were English.
 * A page served at /ar/decision-wheel that is 96% English is not an Arabic
 * page, and asking Google to index it as one has a specific cost — it ranks for
 * an Arabic query, the visitor lands on English, bounces, and Google learns to
 * demote the result. That is strictly worse than not ranking.
 *
 * So a language earns indexation by having content, rather than by existing.
 * The pages still render and still work for anyone who picks that language in
 * the switcher; they simply do not compete in search until they are real.
 */
export const INDEXABLE_LANGS: readonly string[] = [
  // ── Original 11 — fully translated metadata ──────────────────────────────
  'es', 'fr', 'de', 'pt', 'ar', 'ru', 'it', 'ur', 'pl', 'nl',
  // ── Added — metadata translations added below ────────────────────────────
  // High-traffic Asian markets
  'zh', 'ja', 'ko', 'id', 'vi', 'th', 'ms', 'tl',
  // European markets
  'sv', 'cs', 'da', 'fi', 'el', 'hu', 'ro', 'sk', 'bg', 'uk', 'tr',
];


/**
 * How much of a page must be translated before it is offered for indexing.
 *
 *   'none'     — anything in INDEXABLE_LANGS, whatever state its copy is in.
 *   'metadata' — translated <title> AND <meta description> required.
 *   'body'     — also requires translated on-page copy.
 *
 * Set to 'metadata'. The reasoning, recorded so the number is not later a
 * mystery:
 *
 * A page needs a genuine claim to the query it is submitted for, and in search
 * results the title and description ARE the page. A route with both translated
 * looks native to a searcher and delivers a wheel whose interface is genuinely
 * localised — the English marketing prose around it is not what someone who
 * searched for a spinner came to read. That is a real result, not a bait.
 *
 * A route WITHOUT them is a different thing entirely: /ja/gallery would show
 * "Wheel Gallery — Ready-Made Spinner Templates" in English. It cannot win a
 * Japanese query, and it cannot win an English one either because it loses to
 * /gallery, which is the same content on a better URL. Submitting 542 such
 * pages adds no reach and makes a new domain look mostly thin.
 *
 * 'body' would be correct for an article site, where the prose is the product.
 * Here the product is the wheel, and the wheel is translated.
 */
export type TranslationBar = 'none' | 'metadata' | 'body';
export const TRANSLATION_BAR: TranslationBar = 'metadata';

/** Whether a resolved page clears the configured bar. */
export function meetsTranslationBar(c: ResolvedContent): boolean {
  switch (TRANSLATION_BAR) {
    case 'none': return true;
    case 'metadata': return c.translated.title && c.translated.description;
    case 'body': return c.translated.title && c.translated.description && c.translated.body;
  }
}

/**
 * Whether `route` in `lang` should be indexed, listed in the sitemap, and
 * included in the hreflang cluster.
 *
 * Both build paths call this so they cannot disagree: a page excluded from the
 * sitemap but still advertised in hreflang would be an annotation pointing at a
 * URL Google was never given, which invalidates the whole cluster.
 */
export function isIndexableIn(
  lang: string,
  route: string,
  english: { title: string; description: string; h1?: string; body?: string[] },
  defaultLang = 'en',
): boolean {
  if (lang === defaultLang) return true;
  if (!INDEXABLE_LANGS.includes(lang)) return false;
  return meetsTranslationBar(resolveContent(lang, route, english));
}

/**
 * Resolve a route's copy for a language, falling back field by field.
 *
 * Field-level rather than whole-record: a route with a translated title but no
 * translated body should keep the translated title. Falling back as a unit
 * would throw away work that exists.
 *
 * `english` carries the source copy — passed in rather than imported so this
 * module stays a leaf that prerender.mjs can read without pulling in the React
 * tree behind seoData.
 */
export function resolveContent(
  lang: string,
  route: string,
  english: { title: string; description: string; h1?: string; body?: string[] },
): ResolvedContent {
  const loc = SEO_LOCALES[lang]?.[route];
  return {
    title: loc?.title ?? english.title,
    description: loc?.description ?? english.description,
    h1: loc?.h1 ?? loc?.title ?? english.h1 ?? english.title,
    body: loc?.body ?? (loc?.description ? [loc.description] : english.body) ?? [],
    translated: {
      title: !!loc?.title,
      description: !!loc?.description,
      h1: !!(loc?.h1 || loc?.title),
      body: !!(loc?.body?.length || loc?.description),
    },
  };
}

/** lang -> route -> copy. */
export type SeoLocaleMap = Record<string, Record<string, LocalisedSEO>>;

export const SEO_LOCALES: SeoLocaleMap = {
  es: {
    '/': {
      title: 'Selector de Nombres y Ruleta Aleatoria Gratis',
      description: 'Rueda giratoria gratis — agrega nombres y gira para elegir un ganador aleatorio. Ideal para aulas, sorteos, rifas y decisiones.',
    },
    '/classroom-name-picker': {
      title: 'Selector de Nombres para Clase', // SEO-4
      description: 'Selector de nombres gratuito para profesores. Elige alumnos al azar de forma justa. Agrega tu lista de clase y gira la rueda al instante.',
    },
    '/giveaway-wheel': {
      title: 'Rueda de Sorteo — Selector de Ganadores', // SEO-4
      description: 'Realiza un sorteo justo y transparente con nuestra rueda giratoria gratuita. Agrega participantes y gira para elegir un ganador aleatorio.',
    },
    '/yes-no-wheel': {
      title: 'Rueda Sí o No — Tomador de Decisiones', // SEO-4
      description: '¿No puedes decidir? Gira nuestra rueda de Sí o No gratis para una decisión aleatoria instantánea. Ideal para juegos y decisiones cotidianas.',
    },
    '/gallery': {
      title: 'Galería de Plantillas — Ruedas Giratorias', // SEO-4
      description: 'Explora 48 plantillas de rueda giratoria listas para usar. Carga una rueda en un clic: días, dados, sí o no, géneros de cine y más.',
    },
    '/how-to': {
      title: 'Cómo Usar la Rueda Giratoria — Guía', // SEO-4
      description: 'Aprende a usar la rueda giratoria TheNameWheel. Agrega nombres, personaliza la rueda, gira y comparte los resultados.',
    },
    '/faq': {
      title: 'Preguntas Frecuentes — Rueda TheNameWheel',
      description: 'Respuestas a preguntas comunes sobre TheNameWheel. ¿Es verdaderamente aleatorio? ¿Cómo funciona? ¿Puedo guardar mi rueda?',
    },
    '/about': {
      title: 'Acerca de TheNameWheel — Selector Gratuito', // SEO-4
      description: 'TheNameWheel es una herramienta gratuita y privada para elegir nombres al azar, creada para profesores, streamers y cualquiera que necesite una selección justa.',
    },
    '/contact': {
      title: 'Contacto — Escríbenos | TheNameWheel',
      description: '¿Tienes una pregunta, sugerencia o comentario sobre TheNameWheel? Envíanos un mensaje a través del formulario y te responderemos.',
    },
    '/classes': {
      title: 'Tus Clases — Ruedas Guardadas | TheNameWheel',
      description: 'Gestiona todas tus ruedas de clase guardadas: listas de alumnos, historial de giros y quién no ha salido aún. Todo guardado en tu navegador, sin cuentas.',
    },
    '/random-letter-wheel': {
      title: 'Rueda de Letras Aleatorias — Alfabeto Gratis',
      description: 'Gira la rueda del alfabeto para elegir una letra al azar. A-Z, solo vocales o consonantes. Ideal para Scrabble, juegos de palabras y actividades escolares.',
    },
    '/prize-wheel': {
      title: 'Rueda de Premios — Sorteo Online Gratis',
      description: 'Crea una rueda de premios gratis para eventos y sorteos. Añade premios, gira para ganar y usa la eliminación automática. Sin registro, enlace para compartir.',
    },
    '/decision-wheel': {
      title: 'Rueda de Decisiones — Tomador Aleatorio Gratis',
      description: '¿No puedes decidir? Gira la rueda y deja que elija por ti. Añade opciones, gira y obtén una respuesta instantánea. Herramienta gratuita, sin registro.',
    },
    '/trivia-wheel': {
      title: 'Rueda de Trivia — Selector de Categorías Gratis',
      description: 'Gira la rueda de trivia para elegir una categoría al instante. Juego gratuito para noches de preguntas, aulas y fiestas. 10 categorías cargadas, personalizable.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Creador de Rueda de la Fortuna — Gratis Online',
      description: 'Crea tu rueda de la fortuna online en un minuto. Gratis, sin registro. Añade premios o categorías, gira, comparte el enlace o inserta en tu web.',
    },
    '/spin-the-bottle': {
      title: 'Girar la Botella Online — App Gratis para Fiestas',
      description: 'App gratuita de girar la botella para fiestas. Añade jugadores y gira la rueda para elegir uno al azar. Ideal para verdad o reto. Sin descargas.',
    },
    '/number-wheel': {
      title: 'Rueda de Números — Generador Aleatorio Gratis',
      description: 'Rueda de números gratis — elige cualquier rango (1–10, 1–100 o personalizado). Para matemáticas en clase, sorteos y dados digitales. Sin registro.',
    },
    '/random-team-generator': {
      title: 'Generador de Equipos Aleatorios — Rueda Gratis',
      description: 'Divide cualquier grupo en equipos aleatorios y justos en segundos. Herramienta gratuita para profesores, managers y jugadores. Sin registro necesario.',
    },
  },

  fr: {
    '/': {
      // <!-- SEO: French random-wheel landing metadata. -->
      title: 'Roulette Aléatoire — Tirage au Sort de Noms Gratuit | Name Wheel',
      description: 'Faites tourner la roulette aléatoire pour choisir un nom, former des équipes ou prendre une décision. Gratuit, sans inscription.',
    },
    '/classroom-name-picker': {
      title: 'Sélecteur de Noms en Classe Gratuit',
      description: 'Sélecteur de noms gratuit pour les enseignants. Choisissez les élèves équitablement au hasard. Ajoutez votre liste de classe et tournez la roue instantanément.',
    },
    '/giveaway-wheel': {
      title: 'Roue de tirage au sort — picker gratuit | TheNameWheel', // <!-- GSC-7 -->
      description: 'Organisez un tirage au sort en direct — importez votre liste, faites tourner la roue et supprimez automatiquement les gagnants. Gratuit, sans inscription.', // <!-- GSC-7 -->
    },
    '/yes-no-wheel': {
      title: 'Roue Oui ou Non Gratuite',
      description: 'Vous n’arrivez pas à décider ? Tournez notre roue Oui ou Non gratuite pour une décision instantanée. Idéal pour les jeux et la vie quotidienne.',
    },
    '/gallery': {
      title: 'Galerie de Modèles — Roues Aléatoires Prêtes à l’Emploi',
      description: 'Parcourez 48 modèles de roue prêts à l’emploi. Chargez une roue en un clic : jours, dés, oui ou non, genres de films et plus encore.',
    },
    '/how-to': {
      title: 'Comment Utiliser la Roue Aléatoire — Guide Étape par Étape',
      description: 'Apprenez à utiliser la roue TheNameWheel. Ajoutez des noms, personnalisez la roue, tournez et partagez les résultats.',
    },
    '/faq': {
      title: 'Foire Aux Questions — Roue Aléatoire TheNameWheel',
      description: 'Réponses aux questions fréquentes sur TheNameWheel. Est-ce vraiment aléatoire ? Comment ça marche ? Puis-je sauvegarder ma roue ?',
    },
    '/about': {
      title: 'À Propos de TheNameWheel — Sélecteur de Noms Gratuit et Privé',
      description: 'TheNameWheel est un outil gratuit et privé pour tirer des noms au sort, conçu pour les enseignants, les streamers et tous ceux qui ont besoin d’un choix équitable.',
    },
    '/contact': {
      title: 'Contact — Écrivez-nous | TheNameWheel',
      description: 'Vous avez une question, une suggestion ou un retour sur TheNameWheel ? Envoyez-nous un message via notre formulaire de contact.',
    },
    '/classes': {
      title: 'Vos Classes — Roues Sauvegardées | TheNameWheel',
      description: 'Gérez toutes vos roues de classe sauvegardées : listes d\'élèves, historique des tours et qui n\'a pas encore été sélectionné. Stocké dans votre navigateur.',
    },
    '/random-letter-wheel': {
      title: 'Roue de Lettres Aléatoires — Alphabet Gratuit',
      description: 'Faites tourner la roue pour choisir une lettre au hasard. A–Z, voyelles ou consonnes. Parfait pour Scrabble, jeux de mots et activités pédagogiques.',
    },
    '/prize-wheel': {
      title: 'Roue des Lots — Tirage au Sort Gratuit en Ligne',
      description: 'Créez gratuitement une roue des lots pour vos événements et concours. Ajoutez des prix, tournez pour gagner et utilisez la suppression automatique. Sans inscription.',
    },
    '/decision-wheel': {
      title: 'Roue de Décision — Aide au Choix Aléatoire Gratuite',
      description: 'Vous n\'arrivez pas à décider ? Tournez la roue et laissez-la choisir pour vous. Ajoutez vos options et obtenez une réponse instantanée. Gratuit, sans inscription.',
    },
    '/trivia-wheel': {
      title: 'Roue de Trivia — Sélecteur de Catégories Gratuit',
      description: 'Faites tourner la roue pour choisir une catégorie de quiz instantanément. Jeu de trivia gratuit pour soirées quiz, classes et fêtes. Entièrement personnalisable.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Créateur de Roue de la Fortune — Gratuit en Ligne',
      description: 'Créez votre roue de la fortune en ligne en une minute. Gratuit, sans inscription. Ajoutez des prix ou catégories, tournez et partagez le lien.',
    },
    '/spin-the-bottle': {
      title: 'Jeu de la Bouteille en Ligne — Application Gratuite',
      description: 'Application gratuite de jeu de la bouteille pour vos soirées. Ajoutez les joueurs et faites tourner la roue pour en choisir un au hasard. Sans téléchargement.',
    },
    '/number-wheel': {
      title: 'Roue de Nombres — Générateur Aléatoire Gratuit',
      description: 'Roue de nombres gratuite — définissez n\'importe quelle plage (1–10, 1–100 ou personnalisée). Pour les maths en classe, tirages et lancers de dés. Sans inscription.',
    },
    '/random-team-generator': {
      title: 'Générateur d\'Équipes Aléatoires — Outil Gratuit',
      description: 'Divisez n\'importe quel groupe en équipes aléatoires équitables en secondes. Outil gratuit pour enseignants, managers et joueurs. Sans inscription.',
    },
  },

  pt: {
    '/': {
      title: 'Roleta Aleatória Grátis e Sorteador de Nomes',
      description: 'Roleta aleatória grátis — adicione nomes e gire para escolher um vencedor aleatório. Perfeito para salas de aula, sorteios, rifas e decisões.',
    },
    '/classroom-name-picker': {
      title: 'Sorteador de Nomes para Sala de Aula',
      description: 'Sorteador de nomes gratuito para professores. Escolha alunos aleatoriamente de forma justa. Adicione sua lista de turma e gire na hora.',
    },
    '/giveaway-wheel': {
      title: 'Roleta de Sorteio e Ganhadores',
      description: 'Faça um sorteio justo e transparente com nossa roleta gratuita. Adicione participantes e gire para escolher um vencedor aleatório.',
    },
    '/yes-no-wheel': {
      title: 'Roleta Sim ou Não Grátis',
      description: 'Não consegue decidir? Gire nossa roleta Sim ou Não grátis para uma decisão instantânea. Ideal para jogos e decisões do dia a dia.',
    },
    '/gallery': {
      title: 'Galeria de Modelos — Roletas Prontas para Usar',
      description: 'Explore 48 modelos de roleta prontos para usar. Carregue uma roleta em um clique: dias, dados, sim ou não, gêneros de filmes e mais.',
    },
    '/how-to': {
      title: 'Como Usar a Roleta Aleatória — Guia Passo a Passo',
      description: 'Aprenda a usar a roleta TheNameWheel. Adicione nomes, personalize a roleta, gire e compartilhe os resultados.',
    },
    '/faq': {
      title: 'Perguntas Frequentes — Roleta Aleatória TheNameWheel',
      description: 'Respostas para perguntas comuns sobre TheNameWheel. É verdadeiramente aleatório? Como funciona? Posso salvar minha roleta?',
    },
    '/about': {
      title: 'Sobre TheNameWheel — Sorteador de Nomes Gratuito e Privado',
      description: 'TheNameWheel é uma ferramenta gratuita e privada para sortear nomes, criada para professores, streamers e qualquer pessoa que precise de uma escolha justa.',
    },
    '/contact': {
      title: 'Contato — Fale Conosco | TheNameWheel',
      description: 'Tem uma dúvida, sugestão ou feedback sobre o TheNameWheel? Envie uma mensagem pelo nosso formulário de contato e retornaremos em breve.',
    },
    '/classes': {
      title: 'Suas Turmas — Rodas Salvas | TheNameWheel',
      description: 'Gerencie todas as roletas de turma salvas: listas de alunos, histórico de giros e quem ainda não foi escolhido. Tudo salvo no navegador, sem envio de dados.',
    },
    '/random-letter-wheel': {
      title: 'Roleta de Letras Aleatórias — Alfabeto Grátis',
      description: 'Gire a roleta do alfabeto para escolher uma letra aleatória. A–Z, apenas vogais ou consoantes. Ótimo para Scrabble, jogos de palavras e atividades escolares.',
    },
    '/prize-wheel': {
      title: 'Roleta de Prêmios — Sorteio Online Grátis',
      description: 'Crie uma roleta de prêmios grátis para eventos e sorteios. Adicione prêmios, gire para ganhar e use remoção automática para sorteios múltiplos. Sem cadastro.',
    },
    '/decision-wheel': {
      title: 'Roleta de Decisão — Tomador Aleatório Grátis',
      description: 'Não consegue decidir? Gire a roleta e deixe ela escolher por você. Adicione opções, gire e obtenha uma resposta instantânea. Ferramenta gratuita, sem cadastro.',
    },
    '/trivia-wheel': {
      title: 'Roleta de Trivia — Seletor de Categorias Grátis',
      description: 'Gire a roleta de trivia para escolher uma categoria de quiz instantaneamente. Jogo gratuito para noites de quiz, salas de aula e festas. Totalmente personalizável.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Criador de Roleta da Fortuna — Grátis Online',
      description: 'Crie sua roleta da fortuna online em um minuto. Grátis, sem cadastro. Adicione prêmios ou categorias, gire e compartilhe o link ou incorpore no seu site.',
    },
    '/spin-the-bottle': {
      title: 'Girar a Garrafa Online — App Grátis para Festas',
      description: 'App gratuita de girar a garrafa para suas festas. Adicione jogadores e gire a roleta para escolher alguém aleatoriamente. Ideal para verdade ou consequência.',
    },
    '/number-wheel': {
      title: 'Roleta de Números — Gerador Aleatório Grátis',
      description: 'Roleta de números grátis — defina qualquer intervalo (1–10, 1–100 ou personalizado). Para matemática em sala, sorteios e dados digitais. Sem cadastro.',
    },
    '/random-team-generator': {
      title: 'Gerador de Times Aleatórios — Ferramenta Grátis',
      description: 'Divida qualquer grupo em times aleatórios e justos em segundos. Ferramenta gratuita para professores, gestores e jogadores. Sem cadastro necessário.',
    },
  },

  ar: {
    '/': {
      title: 'عجلة عشوائية مجانية ومنتقي الأسماء',
      description: 'عجلة عشوائية مجانية عبر الإنترنت — أضف الأسماء وأدر العجلة لاختيار فائز عشوائي. مثالية للفصول الدراسية والهدايا والسحوبات والقرارات.',
    },
    '/classroom-name-picker': {
      title: 'منتقي أسماء الفصل الدراسي — أداة مجانية للمعلمين',
      description: 'منتقي أسماء مجاني للمعلمين. اختر الطلاب بشكل عشوائي وعادل. أضف قائمة فصلك وأدر العجلة فورًا — لا يلزم تسجيل.',
    },
    '/giveaway-wheel': {
      title: 'عجلة الهدايا — منتقي الفائزين المجاني عبر الإنترنت',
      description: 'أدر سحبًا عادلًا وشفافًا باستخدام عجلتنا العشوائية المجانية. أضف المشاركين وأدر العجلة لاختيار فائز عشوائي.',
    },
    '/yes-no-wheel': {
      title: 'عجلة نعم أو لا — صانع القرار العشوائي المجاني',
      description: 'لا تستطيع اتخاذ قرار؟ أدر عجلة نعم أو لا المجانية للحصول على قرار عشوائي فوري. مثالية للألعاب والقرارات اليومية.',
    },
    '/gallery': {
      title: 'معرض القوالب — عجلات دوارة جاهزة للاستخدام',
      description: 'تصفح 48 قالبًا جاهزًا للعجلة الدوارة. حمّل العجلة بنقرة واحدة: الأيام، النرد، نعم أو لا، أنواع الأفلام والمزيد.',
    },
    '/how-to': {
      title: 'كيفية استخدام العجلة الدوارة — دليل خطوة بخطوة',
      description: 'تعلم كيفية استخدام عجلة TheNameWheel الدوارة. أضف الأسماء، خصص العجلة، أدرها وشارك النتائج.',
    },
    '/faq': {
      title: 'الأسئلة الشائعة — عجلة TheNameWheel الدوارة',
      description: 'إجابات على الأسئلة الشائعة حول TheNameWheel. هل هي عشوائية حقًا؟ كيف تعمل؟ هل يمكنني حفظ عجلتي؟',
    },
    '/about': {
      title: 'حول TheNameWheel — أداة مجانية وخاصة لاختيار الأسماء عشوائيًا',
      description: 'TheNameWheel أداة مجانية وخاصة لاختيار الأسماء عشوائيًا، مصممة للمعلمين والمذيعين وكل من يحتاج إلى اختيار عادل.',
    },
    '/contact': {
      title: 'اتصل بنا | TheNameWheel',
      description: 'هل لديك سؤال أو اقتراح أو ملاحظة حول TheNameWheel؟ أرسل لنا رسالة عبر نموذج الاتصال وسنرد عليك في أقرب وقت.',
    },
    '/classes': {
      title: 'فصولك الدراسية — العجلات المحفوظة | TheNameWheel',
      description: 'أدر جميع عجلاتك الدراسية المحفوظة: قوائم الطلاب، سجل الإدارة ومن لم يُختر بعد. محفوظة في متصفحك، بيانات الطلاب لا تغادر جهازك.',
    },
    '/random-letter-wheel': {
      title: 'عجلة الحروف العشوائية — منتقي أبجدي مجاني',
      description: 'أدر عجلة الأبجدية لاختيار حرف عشوائي. من أ إلى ي، حروف العلة أو الحروف الساكنة. مثالية لألعاب الكلمات والفصول والأنشطة التعليمية.',
    },
    '/prize-wheel': {
      title: 'عجلة الجوائز — سحب حظ مجاني أونلاين',
      description: 'أنشئ عجلة جوائز مجانية لفعالياتك ومسابقاتك. أضف الجوائز، أدر للفوز، واستخدم الإزالة التلقائية للسحوبات المتعددة. بدون تسجيل.',
    },
    '/decision-wheel': {
      title: 'عجلة القرار — صانع قرار عشوائي مجاني',
      description: 'لا تستطيع الاختيار؟ أدر عجلة القرار ودعها تختار عنك. أضف خياراتك، أدر العجلة واحصل على إجابة فورية. مجانًا وبدون تسجيل.',
    },
    '/trivia-wheel': {
      title: 'عجلة المعلومات — منتقي فئات اختبار مجاني',
      description: 'أدر عجلة المعلومات لاختيار فئة اختبار عشوائية فورًا. لعبة مجانية لليالي الأسئلة والفصول والحفلات. 10 فئات جاهزة، قابلة للتخصيص الكامل.',
    },
    '/wheel-of-fortune-maker': {
      title: 'صانع عجلة الحظ — إنشاء مجاني أونلاين',
      description: 'أنشئ عجلة الحظ الخاصة بك أونلاين في دقيقة — مجانًا وبدون تسجيل. أضف جوائز أو فئات، أدر العجلة وشارك الرابط أو ضمّنها في موقعك.',
    },
    '/spin-the-bottle': {
      title: 'لعبة الزجاجة أونلاين — تطبيق حفلات مجاني',
      description: 'تطبيق مجاني للعبة الزجاجة لحفلاتك. أضف اللاعبين وأدر العجلة لاختيار شخص عشوائياً. مثالي للحقيقة أو الجرأة. بدون تنزيل.',
    },
    '/number-wheel': {
      title: 'عجلة الأرقام — مولّد أرقام عشوائية مجاني',
      description: 'عجلة أرقام مجانية — اضبط أي نطاق (1–10، 1–100 أو مخصص). مثالية لألعاب الرياضيات والسحوبات ورمي النرد الرقمي. بدون تسجيل.',
    },
    '/random-team-generator': {
      title: 'مولّد فرق عشوائي — أداة مجانية لتقسيم المجموعات',
      description: 'قسّم أي مجموعة إلى فرق عشوائية عادلة في ثوانٍ. أداة مجانية للمعلمين والمدراء واللاعبين. لا تسجيل مطلوب.',
    },
  },

  ur: {
    '/': {
      title: 'مفت اسپنر وہیل اور رینڈم نام چننے والا ٹول',
      description: 'مفت آن لائن اسپنر وہیل — نام شامل کریں اور رینڈم فاتح چننے کے لیے گھمائیں۔ کلاس روم، انعامات، قرعہ اندازی اور فیصلوں کے لیے موزوں۔',
    },
    '/classroom-name-picker': {
      title: 'کلاس روم نام چننے والا — اساتذہ کے لیے مفت ٹول',
      description: 'اساتذہ کے لیے مفت رینڈم نام چننے والا ٹول۔ طلباء کو غیر جانبدارانہ طریقے سے منتخب کریں۔ اپنی کلاس لسٹ شامل کریں اور فوری وہیل گھمائیں۔',
    },
    '/giveaway-wheel': {
      title: 'انعامی وہیل — مفت آن لائن فاتح چننے والا',
      description: 'ہمارے مفت اسپنر وہیل سے منصفانہ اور شفاف قرعہ اندازی کریں۔ شرکاء شامل کریں اور رینڈم فاتح چننے کے لیے گھمائیں۔',
    },
    '/yes-no-wheel': {
      title: 'ہاں یا نہیں وہیل — مفت رینڈم فیصلہ ساز',
      description: 'فیصلہ نہیں کر پا رہے؟ فوری رینڈم فیصلے کے لیے ہمارا مفت ہاں یا نہیں وہیل گھمائیں۔ کھیلوں اور روزمرہ فیصلوں کے لیے بہترین۔',
    },
    '/gallery': {
      title: 'وہیل ٹیمپلیٹ گیلری — تیار اسپنر وہیل',
      description: '48 تیار اسپنر وہیل ٹیمپلیٹس دیکھیں۔ ایک کلک میں وہیل لوڈ کریں: دن، پانسہ، ہاں یا نہیں، فلمی اقسام اور بہت کچھ۔',
    },
    '/how-to': {
      title: 'اسپنر وہیل کا استعمال کیسے کریں — مرحلہ وار گائیڈ',
      description: 'TheNameWheel اسپنر وہیل کا استعمال سیکھیں۔ نام شامل کریں، وہیل کو اپنی مرضی کے مطابق بنائیں، گھمائیں اور نتائج شیئر کریں۔',
    },
    '/faq': {
      title: 'عام سوالات — TheNameWheel اسپنر وہیل',
      description: 'TheNameWheel کے بارے میں عام سوالات کے جوابات۔ کیا یہ واقعی رینڈم ہے؟ یہ کیسے کام کرتا ہے؟ کیا میں اپنی وہیل محفوظ کر سکتا ہوں؟',
    },
    '/about': {
      title: 'TheNameWheel کے بارے میں — مفت اور نجی رینڈم نام چننے والا ٹول',
      description: 'TheNameWheel اساتذہ، اسٹریمرز اور منصفانہ انتخاب چاہنے والے ہر شخص کے لیے بنایا گیا ایک مفت اور نجی رینڈم نام چننے والا ٹول ہے۔',
    },
    '/contact': {
      title: 'رابطہ کریں | TheNameWheel',
      description: 'TheNameWheel کے بارے میں کوئی سوال، تجویز یا رائے ہے؟ ہمارے رابطہ فارم کے ذریعے پیغام بھیجیں۔',
    },
    '/classes': {
      title: 'آپ کی کلاسیں — محفوظ وہیل | TheNameWheel',
      description: 'اپنی تمام محفوظ کلاس وہیل کا انتظام کریں: طلباء کی فہرستیں، اسپن کی تاریخ اور کون نہیں چنا گیا۔ سب براؤزر میں محفوظ، کوئی ڈیٹا اپلوڈ نہیں۔',
    },
    '/random-letter-wheel': {
      title: 'رینڈم حرف وہیل — حروف تہجی سلیکٹر مفت',
      description: 'حروف تہجی وہیل گھمائیں اور رینڈم حرف منتخب کریں۔ A–Z، صرف مصوتے یا صامتے۔ الفاظ کے کھیلوں اور کلاس سرگرمیوں کے لیے موزوں۔',
    },
    '/prize-wheel': {
      title: 'انعامی وہیل — آن لائن قرعہ اندازی مفت',
      description: 'ایونٹس اور گیو اوے کے لیے مفت انعامی وہیل بنائیں۔ انعامات شامل کریں، گھمائیں اور منصفانہ ملٹی پرائز کے لیے آٹو ہٹانے کا استعمال کریں۔',
    },
    '/decision-wheel': {
      title: 'فیصلہ وہیل — رینڈم فیصلہ ساز مفت',
      description: 'فیصلہ نہیں کر پا رہے؟ وہیل گھمائیں اور اسے چنوائیں۔ اپنے اختیارات شامل کریں، گھمائیں اور فوری جواب پائیں۔ مفت، بغیر رجسٹریشن۔',
    },
    '/trivia-wheel': {
      title: 'ٹریویا وہیل — کوئز کیٹیگری سلیکٹر مفت',
      description: 'فوری رینڈم کوئز کیٹیگری چننے کے لیے ٹریویا وہیل گھمائیں۔ کوئز نائٹ، کلاسوں اور پارٹیوں کے لیے مفت گیم۔ 10 زمرے، مکمل حسب ضرورت۔',
    },
    '/wheel-of-fortune-maker': {
      title: 'قسمت وہیل بنائیں — مفت آن لائن',
      description: 'ایک منٹ میں اپنی قسمت وہیل بنائیں — مفت، بغیر رجسٹریشن۔ انعامات یا زمرے شامل کریں، گھمائیں اور لنک شیئر کریں۔',
    },
    '/spin-the-bottle': {
      title: 'بوتل گھمائیں آن لائن — مفت پارٹی گیم',
      description: 'پارٹیوں کے لیے مفت بوتل گھمانے والی ایپ۔ کھلاڑیوں کے نام شامل کریں اور رینڈم انتخاب کے لیے وہیل گھمائیں۔ سچ یا چیلنج کے لیے موزوں۔',
    },
    '/number-wheel': {
      title: 'نمبر وہیل — مفت رینڈم نمبر جنریٹر',
      description: 'مفت نمبر وہیل — کوئی بھی رینج سیٹ کریں (1–10، 1–100 یا کسٹم)۔ ریاضی کے کھیلوں، قرعہ اندازی اور ڈیجیٹل پانسے کے لیے۔ بغیر سائن اپ کے۔',
    },
    '/random-team-generator': {
      title: 'رینڈم ٹیم جنریٹر — مفت گروپ تقسیم کار',
      description: 'کسی بھی گروپ کو سیکنڈوں میں منصفانہ رینڈم ٹیموں میں تقسیم کریں۔ اساتذہ، منیجرز اور گیمرز کے لیے مفت ٹول۔ بغیر رجسٹریشن کے۔',
    },
  },

  de: {
    '/': {
      title: 'Kostenloses Glücksrad und Zufalls-Namensgenerator',
      description: 'Kostenloses Online-Glücksrad — Namen eintragen und drehen, um einen zufälligen Gewinner zu ziehen. Ideal für Unterricht, Verlosungen und Entscheidungen.',
    },
    '/classroom-name-picker': {
      title: 'Namen-Zufallsgenerator für Lehrer', // SEO-4
      description: 'Kostenloser Namensgenerator für Lehrkräfte. Wählen Sie Schülerinnen und Schüler fair per Zufall aus. Klassenliste eintragen und sofort drehen.',
    },
    '/giveaway-wheel': {
      title: 'Gewinnspiel-Rad — Kostenloser Gewinner', // SEO-4
      description: 'Führen Sie eine faire, nachvollziehbare Verlosung mit unserem kostenlosen Glücksrad durch. Teilnehmer eintragen und für einen Zufallsgewinner drehen.',
    },
    '/yes-no-wheel': {
      title: 'Ja-oder-Nein-Rad — Zufallsentscheidung', // SEO-4
      description: 'Unentschlossen? Drehen Sie unser kostenloses Ja-oder-Nein-Rad für eine sofortige Zufallsentscheidung. Ideal für Spiele und den Alltag.',
    },
    '/gallery': {
      title: 'Vorlagen-Galerie — Fertige Glücksräder',
      description: 'Entdecken Sie 48 fertige Glücksrad-Vorlagen. Rad mit einem Klick laden: Wochentage, Würfel, Ja oder Nein, Filmgenres und mehr.',
    },
    '/how-to': {
      title: 'Glücksrad benutzen — Schritt-für-Schritt', // SEO-4
      description: 'Lernen Sie, wie Sie das TheNameWheel-Glücksrad nutzen. Namen hinzufügen, Rad anpassen, drehen und Ergebnisse teilen.',
    },
    '/faq': {
      title: 'Häufige Fragen — TheNameWheel Glücksrad',
      description: 'Antworten auf häufige Fragen zu TheNameWheel. Ist es wirklich zufällig? Wie funktioniert es? Kann ich mein Rad speichern?',
    },
    '/about': {
      title: 'Über TheNameWheel — Kostenloser Zufallsgenerator',
      description: 'TheNameWheel ist ein kostenloses, privates Werkzeug für zufällige Auswahl — gebaut für Lehrkräfte, Streamer und alle, die eine faire Ziehung brauchen.',
    },
    '/contact': {
      title: 'Kontakt — Schreiben Sie uns | TheNameWheel',
      description: 'Haben Sie Fragen, Wünsche oder Feedback zu TheNameWheel? Senden Sie uns eine Nachricht über unser Kontaktformular.',
    },
    '/classes': {
      title: 'Ihre Klassen — Gespeicherte Räder | TheNameWheel',
      description: 'Verwalten Sie alle gespeicherten Klassenräder: Klassenlisten, Drehverlauf und wer noch nicht dran war. Im Browser gespeichert, keine Daten werden hochgeladen.',
    },
    '/random-letter-wheel': {
      title: 'Buchstabenrad — Zufälliger Buchstabengenerator',
      description: 'Das Alphabetrad drehen und einen zufälligen Buchstaben wählen. A–Z, nur Vokale oder Konsonanten. Für Scrabble, Wortspiele und Unterrichtsaktivitäten.',
    },
    '/prize-wheel': {
      title: 'Gewinnrad — Kostenlose Online-Verlosung',
      description: 'Kostenloses Gewinnrad für Veranstaltungen und Verlosungen. Preise hinzufügen, drehen und Auto-Entfernung für faire Mehrfachverlosungen nutzen. Ohne Anmeldung.',
    },
    '/decision-wheel': {
      title: 'Entscheidungsrad — Kostenloser Zufallsentscheider',
      description: 'Können Sie sich nicht entscheiden? Drehen Sie das Entscheidungsrad und lassen Sie es für Sie wählen. Optionen hinzufügen, drehen, sofortige Antwort erhalten.',
    },
    '/trivia-wheel': {
      title: 'Trivia-Rad — Kostenloser Kategorienwähler',
      description: 'Das Trivia-Rad drehen und sofort eine Quiz-Kategorie auswählen. Kostenloses Quiz-Spiel für Pub Quizzes, Unterricht und Spieleabende. Vollständig anpassbar.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Glücksrad-Ersteller — Kostenlos Online Gestalten',
      description: 'Erstellen Sie Ihr Glücksrad in einer Minute — kostenlos, ohne Anmeldung. Preise oder Kategorien hinzufügen, drehen und Link teilen oder Rad einbetten.',
    },
    '/spin-the-bottle': {
      title: 'Flaschendrehen Online — Kostenlose Partyapp',
      description: 'Kostenlose Flaschendrehen-App für Partys. Spielernamen hinzufügen und das Rad drehen, um zufällig jemanden auszuwählen. Ideal für Wahrheit oder Pflicht.',
    },
    '/number-wheel': {
      title: 'Zahlenrad — Kostenloser Zufallszahlengenerator',
      description: 'Kostenloses Zahlenrad — beliebigen Bereich festlegen (1–10, 1–100 oder benutzerdefiniert). Für Matheübungen, Verlosungen und digitale Würfel. Ohne Anmeldung.',
    },
    '/random-team-generator': {
      title: 'Zufälliger Teamgenerator Kostenlos',
      description: 'Teilen Sie jede Gruppe in Sekunden in faire Zufallsteams. Kostenloses Werkzeug für Lehrkräfte, Projektmanager und Gamer. Ohne Registrierung.',
    },
  },

  it: {
    '/': {
      title: 'Ruota Casuale e Selettore di Nomi Gratis',
      description: 'Ruota casuale gratuita — aggiungi i nomi e gira per estrarre un vincitore a caso. Perfetta per la classe, i giveaway, le lotterie e le decisioni.',
    },
    '/classroom-name-picker': {
      title: 'Estrattore di Nomi per la Classe', // SEO-4
      description: "Estrattore di nomi gratuito per insegnanti. Scegli gli studenti a caso in modo equo. Aggiungi l'elenco della classe e gira la ruota all'istante.",
    },
    '/giveaway-wheel': {
      title: 'Ruota dei Giveaway — Estrattore di Vincitori', // SEO-4
      description: "Organizza un'estrazione equa e trasparente con la nostra ruota gratuita. Aggiungi i partecipanti e gira per scegliere un vincitore casuale.",
    },
    '/yes-no-wheel': {
      title: 'Ruota Sì o No — Decisioni Casuali', // SEO-4
      description: 'Non riesci a decidere? Gira la nostra ruota Sì o No gratuita per una decisione istantanea. Ideale per giochi e scelte di tutti i giorni.',
    },
    '/gallery': {
      title: 'Galleria di Modelli — Ruote Pronte',
      description: "Sfoglia 48 modelli di ruota pronti all'uso. Carica una ruota con un clic: giorni, dadi, sì o no, generi di film e altro.",
    },
    '/how-to': {
      title: 'Come Usare la Ruota Casuale — Guida', // SEO-4
      description: 'Impara a usare la ruota TheNameWheel. Aggiungi nomi, personalizza la ruota, gira e condividi i risultati.',
    },
    '/faq': {
      title: 'Domande Frequenti — Ruota TheNameWheel',
      description: 'Risposte alle domande più comuni su TheNameWheel. È davvero casuale? Come funziona? Posso salvare la mia ruota?',
    },
    '/about': {
      title: 'Chi Siamo — TheNameWheel Selettore Gratuito', // SEO-4
      description: 'TheNameWheel è uno strumento gratuito e privato per estrarre nomi a caso, pensato per insegnanti, streamer e chiunque abbia bisogno di una scelta equa.',
    },
    '/contact': {
      title: 'Contatti — Scrivici | TheNameWheel',
      description: 'Hai una domanda, un suggerimento o un feedback su TheNameWheel? Inviaci un messaggio tramite il modulo di contatto.',
    },
    '/classes': {
      title: 'Le Tue Classi — Ruote Salvate | TheNameWheel',
      description: 'Gestisci tutte le tue ruote di classe salvate: elenchi studenti, storico giri e chi non è ancora stato scelto. Salvato nel browser, nessun dato caricato.',
    },
    '/random-letter-wheel': {
      title: 'Ruota delle Lettere Casuali — Alfabeto Gratuito',
      description: 'Gira la ruota dell\'alfabeto per scegliere una lettera casuale. A–Z, solo vocali o consonanti. Perfetta per Scrabble, giochi di parole e attività scolastiche.',
    },
    '/prize-wheel': {
      title: 'Ruota dei Premi — Estrazione Online Gratuita',
      description: 'Crea gratuitamente una ruota dei premi per eventi e concorsi. Aggiungi i premi, gira per vincere e usa la rimozione automatica per estrazioni multiple.',
    },
    '/decision-wheel': {
      title: 'Ruota delle Decisioni — Scegli a Caso Gratis',
      description: 'Non riesci a decidere? Gira la ruota e lascia che scelga per te. Aggiungi le opzioni, gira e ottieni una risposta immediata. Gratuito, senza registrazione.',
    },
    '/trivia-wheel': {
      title: 'Ruota delle Domande — Selettore di Categorie Gratis',
      description: 'Gira la ruota trivia per scegliere una categoria quiz istantaneamente. Gioco gratuito per serate quiz, classi e feste. 10 categorie caricate, personalizzabile.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Crea Ruota della Fortuna — Gratis Online',
      description: 'Crea la tua ruota della fortuna online in un minuto — gratis, senza registrazione. Aggiungi premi o categorie, gira e condividi il link o incorpora nel sito.',
    },
    '/spin-the-bottle': {
      title: 'Gioco della Bottiglia Online — App Gratuita per Feste',
      description: 'App gratuita per il gioco della bottiglia alle feste. Aggiungi i giocatori e gira la ruota per sceglierne uno a caso. Perfetta per verità o azione.',
    },
    '/number-wheel': {
      title: 'Ruota dei Numeri — Generatore Casuale Gratuito',
      description: 'Ruota dei numeri gratis — imposta qualsiasi intervallo (1–10, 1–100 o personalizzato). Per giochi matematici, estrazioni e dadi digitali. Senza registrazione.',
    },
    '/random-team-generator': {
      title: 'Generatore di Squadre Casuali — Strumento Gratuito',
      description: 'Dividi qualsiasi gruppo in squadre casuali eque in pochi secondi. Gratuito per insegnanti, manager e giocatori. Senza registrazione.',
    },
  },

  nl: {
    '/': {
      // <!-- SEO: Dutch name-wheel landing metadata. -->
      title: 'Namenrad Draaien — Gratis Willekeurige Naamkiezer | Name Wheel',
      description: 'Draai het namenrad om willekeurig een naam te kiezen. Gratis online namenkiezer zonder registratie — perfect voor klassen en teams.',
    },
    '/classroom-name-picker': {
      title: 'Namenkiezer voor de Klas — Gratis Tool', // SEO-4
      description: 'Gratis willekeurige namenkiezer voor leerkrachten. Kies leerlingen eerlijk en willekeurig. Voeg je klaslijst toe en draai direct aan het rad.',
    },
    '/giveaway-wheel': {
      title: 'Verlotingswiel — gratis winnaar kiezen | TheNameWheel', // <!-- GSC-7 -->
      description: 'Organiseer een live verloting — importeer uw deelnemerslijst, draai het wiel en verwijder winnaars automatisch. Gratis en geen account nodig.', // <!-- GSC-7 -->
    },
    '/yes-no-wheel': {
      title: 'Ja-of-Nee-Rad — Gratis Willekeurige Beslisser',
      description: 'Kun je niet kiezen? Draai aan ons gratis Ja-of-Nee-rad voor een directe willekeurige beslissing. Ideaal voor spelletjes en alledaagse keuzes.',
    },
    '/gallery': {
      title: 'Sjabloongalerij — Kant-en-klare Draairaden',
      description: 'Bekijk 48 kant-en-klare radsjablonen. Laad een rad met één klik: dagen, dobbelstenen, ja of nee, filmgenres en meer.',
    },
    '/how-to': {
      title: 'Het Draairad Gebruiken — Stap-voor-Stap', // SEO-4
      description: 'Leer hoe je het TheNameWheel-draairad gebruikt. Voeg namen toe, pas het rad aan, draai en deel de resultaten.',
    },
    '/faq': {
      title: 'Veelgestelde Vragen — TheNameWheel',
      description: 'Antwoorden op veelgestelde vragen over TheNameWheel. Is het echt willekeurig? Hoe werkt het? Kan ik mijn rad opslaan?',
    },
    '/about': {
      title: 'Over TheNameWheel — Gratis Namenkiezer',
      description: 'TheNameWheel is een gratis, privé tool om willekeurig namen te kiezen, gemaakt voor leerkrachten, streamers en iedereen die een eerlijke keuze nodig heeft.',
    },
    '/contact': {
      title: 'Contact — Schrijf Ons | TheNameWheel',
      description: 'Heeft u een vraag, verzoek of feedback over TheNameWheel? Stuur ons een bericht via ons contactformulier.',
    },
    '/classes': {
      title: 'Uw Klassen — Opgeslagen Wielen | TheNameWheel',
      description: 'Beheer al uw opgeslagen klassenwielen: leerlingenlijsten, draaigeschiedenis en wie nog niet is gekozen. Opgeslagen in uw browser, namen verlaten nooit uw apparaat.',
    },
    '/random-letter-wheel': {
      title: 'Willekeurig Letterrad — Gratis Alfabet Kiezer',
      description: 'Draai het alfabet rad om een willekeurige letter te kiezen. A–Z, alleen klinkers of medeklinkers. Ideaal voor Scrabble, woordspelletjes en klasactiviteiten.',
    },
    '/prize-wheel': {
      title: 'Prijzenwiel — Gratis Online Trekking',
      description: 'Maak gratis een prijzenwiel voor evenementen en winacties. Voeg prijzen toe, draai om te winnen en gebruik automatisch verwijderen voor eerlijke trekking.',
    },
    '/decision-wheel': {
      title: 'Beslissingsrad — Gratis Willekeurige Beslissers',
      description: 'Kun je niet kiezen? Draai het beslissingsrad en laat het voor jou kiezen. Voeg opties toe, draai en ontvang een direct antwoord. Gratis, geen aanmelding.',
    },
    '/trivia-wheel': {
      title: 'Trivia Rad — Gratis Quizkategorie Kiezer',
      description: 'Draai het trivia rad om direct een quizcategorie te kiezen. Gratis spel voor pubquizzen, klassen en feestjes. 10 categorieën geladen, volledig aanpasbaar.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Rad van Fortuin Maker — Gratis Online',
      description: 'Maak je eigen rad van fortuin online in een minuut — gratis, geen aanmelding. Voeg prijzen of categorieën toe, draai en deel de link of embed op je site.',
    },
    '/spin-the-bottle': {
      title: 'Flesje Draaien Online — Gratis Partyspel App',
      description: 'Gratis online flesje draaien voor feestjes. Voeg spelersnamen toe en draai het wiel om willekeurig iemand te kiezen. Ideaal voor waarheid of durven.',
    },
    '/number-wheel': {
      title: 'Getallenrad — Gratis Willekeurige Getallengenerator',
      description: 'Gratis getallenrad — stel elk bereik in (1–10, 1–100 of aangepast). Voor wiskundelessen, loterijlotingen en digitale dobbelstenen. Geen aanmelding.',
    },
    '/random-team-generator': {
      title: 'Willekeurige Teamgenerator — Gratis Groepsverdeler',
      description: 'Verdeel elke groep in seconden in eerlijke willekeurige teams. Gratis tool voor leerkrachten, managers en gamers. Geen aanmelding vereist.',
    },
  },

  pl: {
    '/': {
      title: 'Darmowe Koło Fortuny i Losowanie Imion',
      description: 'Darmowe koło fortuny online — dodaj imiona i zakręć, aby wylosować zwycięzcę. Idealne do klasy, konkursów, loterii i codziennych decyzji.',
    },
    '/classroom-name-picker': {
      title: 'Losowanie Uczniów dla Nauczycieli',
      description: 'Darmowe losowanie imion dla nauczycieli. Wybieraj uczniów sprawiedliwie i losowo. Dodaj listę klasy i od razu zakręć kołem.',
    },
    '/giveaway-wheel': {
      title: 'Koło losowania — darmowy losowacz nagród | TheNameWheel', // <!-- GSC-7 -->
      description: 'Przeprowadź losowanie na żywo — dodaj listę uczestników, obróć koło i automatycznie usuń zwycięzców. Darmowe narzędzie do losowania, bez rejestracji.', // <!-- GSC-7 -->
    },
    '/yes-no-wheel': {
      title: 'Koło Tak czy Nie — Darmowy Losowy Decydent',
      description: 'Nie możesz się zdecydować? Zakręć naszym darmowym kołem Tak czy Nie, aby uzyskać natychmiastową losową decyzję. Świetne do gier i codziennych wyborów.',
    },
    '/gallery': {
      title: 'Galeria Szablonów — Gotowe Koła Fortuny',
      description: 'Przeglądaj 48 gotowych szablonów koła. Załaduj koło jednym kliknięciem: dni tygodnia, kostka, tak lub nie, gatunki filmowe i więcej.',
    },
    '/how-to': {
      title: 'Jak Używać Koła Fortuny — Przewodnik Krok po Kroku',
      description: 'Naucz się korzystać z koła TheNameWheel. Dodaj imiona, dostosuj koło, zakręć i udostępnij wyniki.',
    },
    '/faq': {
      title: 'Często Zadawane Pytania — Koło Fortuny TheNameWheel',
      description: 'Odpowiedzi na częste pytania o TheNameWheel. Czy losowanie jest naprawdę losowe? Jak to działa? Czy mogę zapisać moje koło?',
    },
    '/about': {
      title: 'O TheNameWheel — Darmowe i Prywatne Losowanie Imion',
      description: 'TheNameWheel to darmowe, prywatne narzędzie do losowania imion, stworzone dla nauczycieli, streamerów i wszystkich, którzy potrzebują uczciwego wyboru.',
    },
    '/contact': {
      title: 'Kontakt — Napisz do Nas | TheNameWheel',
      description: 'Masz pytanie, propozycję lub opinię na temat TheNameWheel? Wyślij nam wiadomość przez formularz kontaktowy.',
    },
    '/classes': {
      title: 'Twoje Klasy — Zapisane Koła | TheNameWheel',
      description: 'Zarządzaj wszystkimi zapisanymi kołami klasowymi: listami uczniów, historią losowań i kto nie był jeszcze wybierany. Zapisane w przeglądarce bez przesyłania.',
    },
    '/random-letter-wheel': {
      title: 'Koło Losowych Liter — Darmowy Selektor Alfabetu',
      description: 'Zakręć kołem alfabetu, aby wybrać losową literę. A–Z, tylko samogłoski lub spółgłoski. Idealne do Scrabble, gier słownych i ćwiczeń w klasie.',
    },
    '/prize-wheel': {
      title: 'Koło Nagród — Darmowe Losowanie Online',
      description: 'Stwórz darmowe koło nagród na imprezy i losowania. Dodaj nagrody, zakręć, aby wygrać, i użyj auto-usuwania dla uczciwych losowań wielu nagród.',
    },
    '/decision-wheel': {
      title: 'Koło Decyzji — Darmowy Losowy Pomocnik Decyzyjny',
      description: 'Nie możesz się zdecydować? Zakręć kołem i pozwól mu wybrać za ciebie. Dodaj opcje, zakręć i uzyskaj natychmiastową odpowiedź. Darmowe, bez rejestracji.',
    },
    '/trivia-wheel': {
      title: 'Koło Trivia — Darmowy Selektor Kategorii Quizowych',
      description: 'Zakręć kołem trivia, aby natychmiast wybrać kategorię quizu. Darmowa gra dla wieczorów z quizem, klas i imprez. 10 kategorii, w pełni konfigurowalne.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Kreator Koła Fortuny — Darmowy Online',
      description: 'Stwórz własne koło fortuny online w minutę — darmowe, bez rejestracji. Dodaj nagrody lub kategorie, zakręć i udostępnij link lub osadź na stronie.',
    },
    '/spin-the-bottle': {
      title: 'Butelka Online — Darmowa Gra Imprezowa',
      description: 'Darmowa gra w butelkę online na imprezy. Dodaj graczy i zakręć kołem, aby wybrać kogoś losowo. Idealna do prawdy czy wyzwania. Bez pobierania.',
    },
    '/number-wheel': {
      title: 'Koło Liczb — Darmowy Generator Losowych Liczb',
      description: 'Darmowe koło liczb — ustaw dowolny zakres (1–10, 1–100 lub własny). Do matematyki w klasie, losowań i cyfrowej kostki. Bez rejestracji.',
    },
    '/random-team-generator': {
      title: 'Generator Losowych Zespołów — Darmowy Podział',
      description: 'Podziel dowolną grupę na sprawiedliwe losowe zespoły w sekundy. Darmowe narzędzie dla nauczycieli, menedżerów i graczy. Bez rejestracji.',
    },
  },

  ru: {
    '/': {
      title: 'Колесо Фортуны и Выбор Случайных Имён',
      description: 'Бесплатное онлайн-колесо — добавьте имена и крутите, чтобы выбрать случайного победителя. Подходит для класса, розыгрышей, лотерей и решений.',
    },
    '/classroom-name-picker': {
      title: 'Выбор Учеников Случайно — Инструмент', // SEO-4
      description: 'Бесплатный генератор случайных имён для учителей. Выбирайте учеников честно и случайно. Добавьте список класса и сразу крутите колесо.',
    },
    '/giveaway-wheel': {
      title: 'Колесо Розыгрыша — Выбор Победителя', // SEO-4
      description: 'Проведите честный и прозрачный розыгрыш с нашим бесплатным колесом. Добавьте участников и крутите, чтобы выбрать случайного победителя.',
    },
    '/yes-no-wheel': {
      title: 'Колесо Да или Нет — Случайный Выбор', // SEO-4
      description: 'Не можете решить? Крутите наше бесплатное колесо «Да или Нет» для мгновенного случайного решения. Отлично подходит для игр и повседневных выборов.',
    },
    '/gallery': {
      title: 'Галерея Шаблонов — Готовые Колёса',
      description: 'Просмотрите 48 готовых шаблонов колеса. Загрузите колесо одним кликом: дни недели, кубик, да или нет, жанры фильмов и другое.',
    },
    '/how-to': {
      title: 'Как Пользоваться Колесом — Руководство', // SEO-4
      description: 'Научитесь пользоваться колесом TheNameWheel. Добавьте имена, настройте колесо, крутите и делитесь результатами.',
    },
    '/faq': {
      title: 'Частые Вопросы — Колесо TheNameWheel',
      description: 'Ответы на частые вопросы о TheNameWheel. Действительно ли выбор случаен? Как это работает? Можно ли сохранить моё колесо?',
    },
    '/about': {
      title: 'О TheNameWheel — Бесплатный Выбор Имён',
      description: 'TheNameWheel — бесплатный приватный инструмент для случайного выбора имён, созданный для учителей, стримеров и всех, кому нужен честный выбор.',
    },
    '/contact': {
      title: 'Контакты — Напишите Нам | TheNameWheel',
      description: 'Есть вопрос, предложение или отзыв о TheNameWheel? Отправьте нам сообщение через форму обратной связи.',
    },
    '/classes': {
      title: 'Ваши Классы — Сохранённые Колёса | TheNameWheel',
      description: 'Управляйте сохранёнными классными колёсами: списками учеников, историей вращений и кто ещё не выбирался. Хранится в браузере без загрузки данных.',
    },
    '/random-letter-wheel': {
      title: 'Колесо Случайных Букв — Алфавит Бесплатно',
      description: 'Крутите алфавитное колесо для выбора случайной буквы. А–Я (или А–Z), только гласные или согласные. Для словесных игр и школьных занятий.',
    },
    '/prize-wheel': {
      title: 'Колесо Призов — Онлайн-розыгрыш Бесплатно',
      description: 'Бесплатное колесо призов для мероприятий и розыгрышей. Добавьте призы, крутите для выигрыша, используйте автоудаление для честных розыгрышей.',
    },
    '/decision-wheel': {
      title: 'Колесо Решений — Случайный Выбор Бесплатно',
      description: 'Не можете решить? Крутите колесо и пусть оно выберет за вас. Добавьте варианты, крутите и получите мгновенный ответ. Бесплатно, без регистрации.',
    },
    '/trivia-wheel': {
      title: 'Колесо Викторины — Выбор Категории Бесплатно',
      description: 'Крутите колесо викторины для мгновенного выбора категории. Бесплатная игра для вечеринок, уроков и игровых вечеров. 10 категорий, полная настройка.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Создать Колесо Фортуны — Бесплатно Онлайн',
      description: 'Создайте своё колесо фортуны онлайн за минуту — бесплатно, без регистрации. Добавьте призы или категории, крутите и делитесь ссылкой.',
    },
    '/spin-the-bottle': {
      title: 'Бутылочка Онлайн — Бесплатная Игра для Вечеринки',
      description: 'Бесплатная онлайн-бутылочка для вечеринок. Добавьте игроков и крутите колесо для случайного выбора. Отлично подходит для «правда или действие».',
    },
    '/number-wheel': {
      title: 'Колесо Чисел — Случайный Генератор Чисел',
      description: 'Бесплатное колесо чисел — задайте любой диапазон (1–10, 1–100 или свой). Для математических игр, розыгрышей и цифровых кубиков. Без регистрации.',
    },
    '/random-team-generator': {
      title: 'Генератор Случайных Команд — Бесплатный',
      description: 'Разделите любую группу на случайные справедливые команды за секунды. Бесплатно для учителей, менеджеров и геймеров. Без регистрации.',
    },
  },

  zh: {
    '/': {
      title: '免费随机转盘 — 随机抽名工具',
      description: '免费在线随机转盘 — 添加名字并旋转，随机抽取获奖者。适用于课堂、抽奖、赠品和日常决策。',
    },
    '/classroom-name-picker': {
      title: '课堂随机点名器 — 教师免费工具',
      description: '免费的课堂点名工具。公平随机地抽取学生。添加班级名单，立即旋转转盘。',
    },
    '/giveaway-wheel': {
      title: '抽奖转盘 — 免费随机抽奖工具',
      description: '使用我们的免费随机转盘进行公平透明的抽奖。添加参与者，旋转以随机选出获奖者。',
    },
    '/yes-no-wheel': {
      title: '是或否转盘 — 免费随机决策工具',
      description: '无法决定？旋转我们的免费是或否转盘，即刻获得随机答案。适合游戏和日常选择。',
    },
    '/gallery': {
      title: '模板库 — 预设随机转盘',
      description: '浏览48个即用型转盘模板。一键加载：星期、骰子、是或否、电影类型等。',
    },
    '/how-to': {
      title: '如何使用随机转盘 — 操作指南',
      description: '学习使用TheNameWheel随机转盘。添加名字、自定义转盘、旋转并分享结果。',
    },
    '/faq': {
      title: '常见问题 — TheNameWheel随机转盘',
      description: '关于TheNameWheel的常见问题解答。结果真的随机吗？如何使用？能保存我的转盘吗？',
    },
    '/about': {
      title: '关于TheNameWheel — 免费随机抽名工具',
      description: 'TheNameWheel是一款免费私密的随机抽名工具，专为教师、主播和需要公平选择的人设计。',
    },
    '/contact': {
      title: '联系我们 | TheNameWheel',
      description: '对TheNameWheel有疑问、功能请求或反馈？通过我们的联系表单发送消息，我们将尽快回复您。',
    },
    '/classes': {
      title: '我的班级 — 已保存的转盘 | TheNameWheel',
      description: '管理所有已保存的班级转盘：学生名单、旋转历史记录和尚未被选到的学生。存储在浏览器中，学生姓名不会离开您的设备。',
    },
    '/random-letter-wheel': {
      title: '随机字母转盘 — 免费字母选择器',
      description: '转动字母转盘随机选取一个字母。A–Z、仅元音或仅辅音。适用于拼字游戏、文字游戏和课堂活动。',
    },
    '/prize-wheel': {
      title: '奖品转盘 — 免费在线抽奖',
      description: '为活动和抽奖创建免费奖品转盘。添加奖品，旋转赢奖，使用自动移除功能进行公平的多轮抽奖。无需注册。',
    },
    '/decision-wheel': {
      title: '决策转盘 — 免费随机选择工具',
      description: '无法决定？转动决策转盘，让它帮你选择。添加选项，旋转并立即获得答案。免费随机决策工具，无需注册。',
    },
    '/trivia-wheel': {
      title: '知识竞赛转盘 — 免费题目类别选择器',
      description: '旋转知识竞赛转盘，即刻随机选择测验类别。适用于酒吧竞赛、课堂和游戏之夜的免费游戏。10个类别，完全可定制。',
    },
    '/wheel-of-fortune-maker': {
      title: '幸运转盘制作器 — 免费在线创建',
      description: '一分钟内在线创建自己的幸运转盘 — 免费，无需注册。添加奖品或类别，旋转并分享链接或嵌入网站。',
    },
    '/spin-the-bottle': {
      title: '转瓶子游戏 — 免费在线派对游戏',
      description: '免费在线转瓶子游戏，适合派对使用。添加玩家姓名，转动转盘随机选人。非常适合真心话大冒险。无需下载。',
    },
    '/number-wheel': {
      title: '数字转盘 — 免费随机数字生成器',
      description: '免费数字转盘 — 设置任意范围（1–10、1–100或自定义）。适用于数学游戏、抽奖和掷骰子。无需注册。',
    },
    '/random-team-generator': {
      title: '随机团队生成器 — 免费分组工具',
      description: '几秒钟内将任何群体随机分成公平的团队。免费工具，适合教师、项目经理和游戏玩家。无需注册。',
    },
  },

  ja: {
    '/': {
      title: '無料ランダムスピナーと名前抽選ツール',
      description: '無料のオンラインスピナー — 名前を追加してスピン。クラス、プレゼント企画、くじ、決断に最適。',
    },
    '/classroom-name-picker': {
      title: '授業用ランダム名前抽選 — 教師向け無料ツール',
      description: '教師向け無料名前抽選ツール。公平にランダムで生徒を選べます。クラスリストを追加してすぐにスピン。',
    },
    '/giveaway-wheel': {
      title: 'プレゼント抽選スピナー — 無料当選者選択',
      description: '無料スピナーで公平・透明なプレゼント抽選を。参加者を追加してスピンし、ランダムに当選者を決定。',
    },
    '/yes-no-wheel': {
      title: 'はいかいいえスピナー — 無料ランダム決断',
      description: '迷ったら？無料のはい/いいえスピナーで即座にランダムな答えを。ゲームや日常の決断に最適。',
    },
    '/gallery': {
      title: 'テンプレートギャラリー — すぐ使えるスピナー',
      description: '48種類のすぐ使えるスピナーテンプレートを探索。曜日、サイコロ、はい/いいえ、映画ジャンルなどワンクリックで。',
    },
    '/how-to': {
      title: 'スピナーの使い方 — ステップバイステップ',
      description: 'TheNameWheelの使い方。名前を追加、カスタマイズして、スピンして結果をシェア。',
    },
    '/faq': {
      title: 'よくある質問 — TheNameWheelスピナー',
      description: 'TheNameWheelに関するよくある質問。本当にランダム？仕組みは？スピナーを保存できる？',
    },
    '/about': {
      title: 'TheNameWheelについて — 無料名前抽選ツール',
      description: 'TheNameWheelは、教師・配信者・公平な選択が必要な誰もが使える無料のプライベート名前抽選ツールです。',
    },
    '/contact': {
      title: 'お問い合わせ | TheNameWheel',
      description: 'TheNameWheelについてご質問・ご要望・フィードバックがありますか？お問い合わせフォームからメッセージをお送りください。',
    },
    '/classes': {
      title: 'あなたのクラス — 保存済みホイール | TheNameWheel',
      description: '保存したクラスホイールをすべて管理：出席リスト、スピン履歴、まだ選ばれていない生徒を確認。ブラウザに保存され、データはデバイスの外に出ません。',
    },
    '/random-letter-wheel': {
      title: 'ランダム文字ホイール — 無料アルファベット選択',
      description: 'アルファベットホイールを回してランダムに文字を選択。A–Z・母音のみ・子音のみに対応。ワードゲーム・スクラブル・授業活動に最適。',
    },
    '/prize-wheel': {
      title: 'プライズホイール — 無料オンライン抽選',
      description: 'イベントやプレゼント企画向け無料プライズホイール。賞品を追加してスピン、オートリムーブで公平な複数賞品抽選も。登録不要。',
    },
    '/decision-wheel': {
      title: 'デシジョンホイール — 無料ランダム意思決定',
      description: '迷ったら？ デシジョンホイールを回して選んでもらおう。選択肢を追加してスピン、即座に答えが出る。無料・登録不要。',
    },
    '/trivia-wheel': {
      title: 'トリビアホイール — 無料クイズカテゴリー選択',
      description: 'トリビアホイールを回してクイズのカテゴリーを即座に選択。クイズナイト・授業・パーティー向け無料ゲーム。10カテゴリー搭載、完全カスタマイズ可能。',
    },
    '/wheel-of-fortune-maker': {
      title: 'フォーチュンホイール作成 — 無料オンライン',
      description: '1分でオリジナルのフォーチュンホイールを作成 — 無料・登録不要。賞品やカテゴリーを追加してスピン、リンクを共有またはサイトに埋め込み。',
    },
    '/spin-the-bottle': {
      title: 'ボトルスピン — 無料オンラインパーティーゲーム',
      description: 'パーティー向け無料ボトルスピンゲーム。プレイヤーの名前を追加してスピンし、ランダムに選ぼう。真実か挑戦ゲームに最適。ダウンロード不要。',
    },
    '/number-wheel': {
      title: '数字スピナー — 無料ランダム数字ジェネレーター',
      description: '無料の数字スピナー — 任意の範囲（1–10、1–100またはカスタム）を設定。数学ゲーム、抽選、サイコロに最適。登録不要。',
    },
    '/random-team-generator': {
      title: 'ランダムチーム分け — 無料グループ分けツール',
      description: 'どんなグループも数秒で公平なランダムチームに分割。先生・マネージャー・ゲーマー向け無料ツール。登録不要。',
    },
  },

  ko: {
    '/': {
      title: '무료 랜덤 스피너 및 이름 뽑기 도구',
      description: '무료 온라인 스피너 — 이름을 추가하고 돌려서 무작위 당첨자를 선택하세요. 교실, 이벤트, 추첨, 결정에 완벽합니다.',
    },
    '/classroom-name-picker': {
      title: '수업용 무작위 이름 뽑기 — 교사 무료 도구',
      description: '교사를 위한 무료 무작위 이름 뽑기 도구. 학생을 공정하고 무작위로 선택하세요. 학급 명단을 추가하고 바로 돌리세요.',
    },
    '/giveaway-wheel': {
      title: '경품 추첨 스피너 — 무료 당첨자 선택기',
      description: '무료 스피너로 공정하고 투명한 경품 추첨을 진행하세요. 참가자를 추가하고 돌려서 무작위 당첨자를 선택하세요.',
    },
    '/yes-no-wheel': {
      title: '예 아니오 스피너 — 무료 랜덤 결정 도구',
      description: '결정하기 어려우신가요? 무료 예/아니오 스피너를 돌려 즉각적인 무작위 답을 얻으세요. 게임과 일상 선택에 이상적입니다.',
    },
    '/gallery': {
      title: '템플릿 갤러리 — 바로 사용할 수 있는 스피너',
      description: '48개의 바로 사용 가능한 스피너 템플릿을 탐색하세요. 요일, 주사위, 예/아니오, 영화 장르 등을 클릭 한 번으로.',
    },
    '/how-to': {
      title: '랜덤 스피너 사용 방법 — 단계별 가이드',
      description: 'TheNameWheel 사용법을 배워보세요. 이름을 추가하고, 스피너를 커스터마이즈하고, 돌리고, 결과를 공유하세요.',
    },
    '/faq': {
      title: '자주 묻는 질문 — TheNameWheel 스피너',
      description: 'TheNameWheel에 대한 자주 묻는 질문. 정말 무작위인가요? 어떻게 작동하나요? 스피너를 저장할 수 있나요?',
    },
    '/about': {
      title: 'TheNameWheel 소개 — 무료 이름 뽑기 도구',
      description: 'TheNameWheel은 교사, 스트리머, 공정한 선택이 필요한 모든 이를 위한 무료 비공개 이름 뽑기 도구입니다.',
    },
    '/contact': {
      title: '문의하기 | TheNameWheel',
      description: 'TheNameWheel에 대한 질문, 기능 요청, 또는 피드백이 있으신가요? 문의 양식을 통해 메시지를 보내주세요.',
    },
    '/classes': {
      title: '내 학급 — 저장된 스피너 | TheNameWheel',
      description: '저장된 모든 학급 스피너를 관리하세요: 학생 명단, 스핀 기록, 아직 선택되지 않은 학생. 브라우저에 저장되며 학생 이름이 기기 밖으로 나가지 않습니다.',
    },
    '/random-letter-wheel': {
      title: '랜덤 글자 스피너 — 무료 알파벳 선택기',
      description: '알파벳 스피너를 돌려 랜덤 글자를 선택하세요. A–Z, 모음만 또는 자음만 선택 가능. 단어 게임, 스크래블, 교실 활동에 최적.',
    },
    '/prize-wheel': {
      title: '상품 스피너 — 무료 온라인 추첨',
      description: '이벤트와 경품 행사를 위한 무료 상품 스피너를 만드세요. 상품 추가 후 돌리고, 자동 제거 기능으로 공정한 다중 상품 추첨도 가능. 회원가입 불필요.',
    },
    '/decision-wheel': {
      title: '결정 스피너 — 무료 랜덤 선택 도구',
      description: '결정하기 어려우신가요? 스피너를 돌려 선택을 맡기세요. 옵션을 추가하고 돌리면 즉각적인 답변을 얻을 수 있어요. 무료, 회원가입 불필요.',
    },
    '/trivia-wheel': {
      title: '퀴즈 스피너 — 무료 카테고리 선택기',
      description: '퀴즈 카테고리를 즉시 무작위로 선택하려면 퀴즈 스피너를 돌리세요. 퀴즈 나이트, 수업, 파티를 위한 무료 게임. 10개 카테고리, 완전 커스터마이즈 가능.',
    },
    '/wheel-of-fortune-maker': {
      title: '포춘 휠 만들기 — 무료 온라인',
      description: '1분 안에 나만의 포춘 휠을 만드세요 — 무료, 회원가입 불필요. 상품이나 카테고리를 추가하고 돌려서 링크를 공유하거나 사이트에 삽입하세요.',
    },
    '/spin-the-bottle': {
      title: '병 돌리기 온라인 — 무료 파티 게임',
      description: '파티를 위한 무료 병 돌리기 앱. 플레이어 이름을 추가하고 스피너를 돌려 무작위로 선택하세요. 진실 또는 도전 게임에 완벽. 다운로드 불필요.',
    },
    '/number-wheel': {
      title: '숫자 스피너 — 무료 랜덤 숫자 생성기',
      description: '무료 숫자 스피너 — 범위 설정 가능 (1–10, 1–100 또는 사용자 지정). 수학 게임, 추첨, 주사위 대용으로 사용. 회원가입 불필요.',
    },
    '/random-team-generator': {
      title: '무작위 팀 생성기 — 무료 그룹 나누기 도구',
      description: '몇 초 만에 그룹을 공정한 무작위 팀으로 나누세요. 교사, 매니저, 게이머를 위한 무료 도구. 회원가입 불필요.',
    },
  },

  tr: {
    '/': {
      title: 'Ücretsiz Rastgele Çark ve İsim Seçici',
      description: 'Ücretsiz çevrimiçi çark — isim ekleyin ve rastgele kazananı seçmek için çevirin. Sınıf, çekiliş, piyango ve karar vermek için mükemmel.',
    },
    '/classroom-name-picker': {
      title: 'Sınıf İsim Seçici — Öğretmenlere Ücretsiz',
      description: 'Öğretmenler için ücretsiz rastgele isim seçici. Öğrencileri adil ve rastgele seçin. Sınıf listesini ekleyin ve hemen çevirin.',
    },
    '/giveaway-wheel': {
      title: 'Çekiliş Çarkı — Ücretsiz Kazanan Seçici',
      description: 'Ücretsiz çarkımızla adil ve şeffaf bir çekiliş yapın. Katılımcıları ekleyin ve rastgele kazananı seçmek için çevirin.',
    },
    '/yes-no-wheel': {
      title: 'Evet ya da Hayır Çarkı — Ücretsiz Karar Aracı',
      description: 'Karar veremez misiniz? Anında rastgele cevap için ücretsiz Evet/Hayır çarkımızı çevirin. Oyunlar ve günlük seçimler için idealdir.',
    },
    '/gallery': {
      title: 'Şablon Galerisi — Hazır Rastgele Çarklar',
      description: 'Kullanıma hazır 48 çark şablonuna göz atın. Bir tıkla çark yükleyin: günler, zar, evet/hayır, film türleri ve daha fazlası.',
    },
    '/how-to': {
      title: 'Rastgele Çark Nasıl Kullanılır — Adım Adım',
      description: 'TheNameWheel çarkını nasıl kullanacağınızı öğrenin. İsim ekleyin, çarkı özelleştirin, çevirin ve sonuçları paylaşın.',
    },
    '/faq': {
      title: 'Sık Sorulan Sorular — TheNameWheel Çarkı',
      description: 'TheNameWheel hakkında sık sorulan sorulara cevaplar. Gerçekten rastgele mi? Nasıl çalışır? Çarkımı kaydedebilir miyim?',
    },
    '/about': {
      title: 'TheNameWheel Hakkında — Ücretsiz İsim Seçici',
      description: "TheNameWheel, öğretmenler, yayıncılar ve adil seçim gerektiren herkes için ücretsiz, gizli bir rastgele isim seçici aracıdır.",
    },
    '/contact': {
      title: 'İletişim — Bize Yazın | TheNameWheel',
      description: 'TheNameWheel hakkında bir sorunuz, özellik isteğiniz veya geri bildiriminiz mi var? İletişim formumuzu kullanarak bize mesaj gönderin.',
    },
    '/classes': {
      title: 'Sınıflarınız — Kayıtlı Çarklar | TheNameWheel',
      description: 'Tüm kayıtlı sınıf çarklarınızı yönetin: öğrenci listeleri, döndürme geçmişi ve henüz seçilmeyenler. Tarayıcınızda saklanır, öğrenci adları cihazınızı terk etmez.',
    },
    '/random-letter-wheel': {
      title: 'Rastgele Harf Çarkı — Ücretsiz Alfabe Seçici',
      description: 'Rastgele bir harf seçmek için alfabe çarkını döndürün. A–Z, yalnızca ünlüler veya ünsüzler. Scrabble, kelime oyunları ve sınıf etkinlikleri için idealdir.',
    },
    '/prize-wheel': {
      title: 'Ödül Çarkı — Ücretsiz Online Çekiliş',
      description: 'Etkinlikler ve hediye çekilişleri için ücretsiz ödül çarkı oluşturun. Ödüller ekleyin, döndürün ve otomatik kaldırma ile adil çekilişler yapın. Kayıt gerekmez.',
    },
    '/decision-wheel': {
      title: 'Karar Çarkı — Ücretsiz Rastgele Karar Yardımcısı',
      description: 'Karar veremez misiniz? Çarkı döndürün ve sizin için seçmesine izin verin. Seçeneklerinizi ekleyin, döndürün ve anında yanıt alın. Ücretsiz, kayıt gerekmez.',
    },
    '/trivia-wheel': {
      title: 'Trivia Çarkı — Ücretsiz Quiz Kategori Seçici',
      description: 'Anında rastgele bir quiz kategorisi seçmek için trivia çarkını döndürün. Pub quizleri, sınıflar ve oyun geceleri için ücretsiz oyun. 10 kategori, tam özelleştirme.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Çark Yapıcı — Ücretsiz Online Çark Oluştur',
      description: 'Bir dakikada kendi çarkını oluşturun — ücretsiz, kayıt gerektirmez. Ödüller veya kategoriler ekleyin, döndürün ve bağlantıyı paylaşın veya siteye gömin.',
    },
    '/spin-the-bottle': {
      title: 'Şişe Çevirmece Online — Ücretsiz Parti Oyunu',
      description: 'Partiler için ücretsiz şişe çevirmece uygulaması. Oyuncu adlarını ekleyin ve rasgele seçmek için çarkı döndürün. Doğru mu Cesaret mi için mükemmel.',
    },
    '/number-wheel': {
      title: 'Sayı Çarkı — Ücretsiz Rastgele Sayı Üretici',
      description: 'Ücretsiz sayı çarkı — herhangi bir aralık belirleyin (1–10, 1–100 veya özel). Matematik oyunları, çekilişler ve dijital zar için. Kayıt gerekmez.',
    },
    '/random-team-generator': {
      title: 'Rastgele Takım Oluşturucu — Ücretsiz Grup Ayırıcı',
      description: 'Saniyeler içinde herhangi bir grubu adil rastgele takımlara bölün. Öğretmenler, yöneticiler ve oyuncular için ücretsiz araç. Kayıt gerekmez.',
    },
  },

  id: {
    '/': {
      title: 'Roda Putar Acak Gratis dan Pemilih Nama',
      description: 'Roda putar acak gratis — tambahkan nama dan putar untuk memilih pemenang secara acak. Sempurna untuk kelas, hadiah, undian, dan keputusan.',
    },
    '/classroom-name-picker': {
      title: 'Pemilih Nama Kelas — Alat Gratis untuk Guru',
      description: 'Alat pemilih nama acak gratis untuk guru. Pilih siswa secara adil dan acak. Tambahkan daftar kelas dan putar sekarang.',
    },
    '/giveaway-wheel': {
      title: 'Roda Giveaway — Pemilih Pemenang Gratis',
      description: 'Lakukan giveaway yang adil dan transparan dengan roda putar gratis kami. Tambahkan peserta dan putar untuk memilih pemenang secara acak.',
    },
    '/yes-no-wheel': {
      title: 'Roda Ya atau Tidak — Pengambil Keputusan Acak',
      description: 'Tidak bisa memutuskan? Putar roda Ya atau Tidak gratis kami untuk keputusan acak seketika. Ideal untuk permainan dan pilihan sehari-hari.',
    },
    '/gallery': {
      title: 'Galeri Template — Roda Putar Siap Pakai',
      description: 'Jelajahi 48 template roda putar siap pakai. Muat roda dalam satu klik: hari, dadu, ya atau tidak, genre film, dan lainnya.',
    },
    '/how-to': {
      title: 'Cara Menggunakan Roda Putar — Panduan',
      description: 'Pelajari cara menggunakan roda TheNameWheel. Tambahkan nama, sesuaikan roda, putar, dan bagikan hasilnya.',
    },
    '/faq': {
      title: 'Pertanyaan Umum — Roda Putar TheNameWheel',
      description: 'Jawaban atas pertanyaan umum tentang TheNameWheel. Apakah benar-benar acak? Bagaimana cara kerjanya? Bisakah saya menyimpan roda saya?',
    },
    '/about': {
      title: 'Tentang TheNameWheel — Pemilih Nama Gratis',
      description: 'TheNameWheel adalah alat pemilih nama acak yang gratis dan privat, dibuat untuk guru, streamer, dan siapa pun yang membutuhkan pilihan yang adil.',
    },
    '/contact': {
      title: 'Hubungi Kami | TheNameWheel',
      description: 'Punya pertanyaan, permintaan fitur, atau masukan tentang TheNameWheel? Kirim pesan melalui formulir kontak kami.',
    },
    '/classes': {
      title: 'Kelas Anda — Roda Tersimpan | TheNameWheel',
      description: 'Kelola semua roda kelas Anda yang tersimpan: daftar siswa, riwayat putar, dan siapa yang belum terpilih. Tersimpan di browser Anda, nama siswa tidak pernah meninggalkan perangkat.',
    },
    '/random-letter-wheel': {
      title: 'Roda Huruf Acak — Pemilih Alfabet Gratis',
      description: 'Putar roda alfabet untuk memilih huruf secara acak. A–Z, hanya vokal, atau konsonan. Cocok untuk Scrabble, permainan kata, dan kegiatan kelas.',
    },
    '/prize-wheel': {
      title: 'Roda Hadiah — Undian Online Gratis',
      description: 'Buat roda hadiah gratis untuk acara dan undian. Tambahkan hadiah, putar untuk menang, gunakan penghapusan otomatis untuk undian multi-hadiah yang adil.',
    },
    '/decision-wheel': {
      title: 'Roda Keputusan — Pembuat Keputusan Acak Gratis',
      description: 'Tidak bisa memutuskan? Putar roda keputusan dan biarkan ia memilih untuk Anda. Tambahkan pilihan, putar, dan dapatkan jawaban instan. Gratis, tanpa pendaftaran.',
    },
    '/trivia-wheel': {
      title: 'Roda Trivia — Pemilih Kategori Kuis Gratis',
      description: 'Putar roda trivia untuk memilih kategori kuis secara acak seketika. Game gratis untuk malam kuis, kelas, dan pesta. 10 kategori dimuat, sepenuhnya dapat dikustomisasi.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Pembuat Roda Keberuntungan — Gratis Online',
      description: 'Buat roda keberuntungan Anda sendiri secara online dalam satu menit — gratis, tanpa pendaftaran. Tambahkan hadiah atau kategori, putar, dan bagikan tautan.',
    },
    '/spin-the-bottle': {
      title: 'Putar Botol Online — Aplikasi Pesta Gratis',
      description: 'Aplikasi putar botol gratis untuk pesta. Tambahkan nama pemain dan putar roda untuk memilih seseorang secara acak. Cocok untuk truth or dare. Tanpa unduhan.',
    },
    '/number-wheel': {
      title: 'Roda Angka — Generator Angka Acak Gratis',
      description: 'Roda angka gratis — atur rentang apa pun (1–10, 1–100 atau kustom). Untuk permainan matematika, undian, dan dadu digital. Tanpa pendaftaran.',
    },
    '/random-team-generator': {
      title: 'Generator Tim Acak — Alat Gratis Pembagi Grup',
      description: 'Bagi kelompok menjadi tim acak yang adil dalam hitungan detik. Alat gratis untuk guru, manajer, dan gamer. Tanpa pendaftaran.',
    },
  },

  vi: {
    '/': {
      title: 'Vòng Quay Ngẫu Nhiên Miễn Phí và Chọn Tên',
      description: 'Vòng quay ngẫu nhiên miễn phí — thêm tên và quay để chọn người thắng cuộc ngẫu nhiên. Hoàn hảo cho lớp học, quà tặng, bốc thăm và quyết định.',
    },
    '/classroom-name-picker': {
      title: 'Bốc Thăm Tên Học Sinh — Công Cụ Miễn Phí',
      description: 'Công cụ bốc thăm tên miễn phí cho giáo viên. Chọn học sinh công bằng và ngẫu nhiên. Thêm danh sách lớp và quay ngay.',
    },
    '/giveaway-wheel': {
      title: 'Vòng Quay Bốc Thăm — Chọn Người Thắng Miễn Phí',
      description: 'Tổ chức bốc thăm công bằng và minh bạch với vòng quay miễn phí. Thêm người tham gia và quay để chọn người thắng ngẫu nhiên.',
    },
    '/yes-no-wheel': {
      title: 'Vòng Quay Có hay Không — Quyết Định Ngẫu Nhiên',
      description: 'Không thể quyết định? Quay vòng quay Có/Không miễn phí để có câu trả lời ngẫu nhiên ngay lập tức. Lý tưởng cho trò chơi và lựa chọn hàng ngày.',
    },
    '/gallery': {
      title: 'Thư Viện Mẫu — Vòng Quay Sẵn Dùng',
      description: 'Khám phá 48 mẫu vòng quay sẵn dùng. Tải vòng quay chỉ một clic: các ngày, xúc xắc, có/không, thể loại phim và nhiều hơn nữa.',
    },
    '/how-to': {
      title: 'Cách Sử Dụng Vòng Quay Ngẫu Nhiên — Hướng Dẫn',
      description: 'Tìm hiểu cách sử dụng vòng quay TheNameWheel. Thêm tên, tùy chỉnh vòng quay, quay và chia sẻ kết quả.',
    },
    '/faq': {
      title: 'Câu Hỏi Thường Gặp — Vòng Quay TheNameWheel',
      description: 'Trả lời các câu hỏi phổ biến về TheNameWheel. Có thực sự ngẫu nhiên không? Hoạt động như thế nào? Tôi có thể lưu vòng quay không?',
    },
    '/about': {
      title: 'Về TheNameWheel — Công Cụ Chọn Tên Miễn Phí',
      description: 'TheNameWheel là công cụ chọn tên ngẫu nhiên miễn phí và riêng tư, được tạo ra cho giáo viên, streamer và bất kỳ ai cần lựa chọn công bằng.',
    },
    '/contact': {
      title: 'Liên Hệ | TheNameWheel',
      description: 'Bạn có câu hỏi, yêu cầu tính năng hoặc phản hồi về TheNameWheel? Gửi tin nhắn qua biểu mẫu liên hệ của chúng tôi.',
    },
    '/classes': {
      title: 'Lớp Học Của Bạn — Vòng Quay Đã Lưu | TheNameWheel',
      description: 'Quản lý tất cả vòng quay lớp đã lưu: danh sách học sinh, lịch sử quay và ai chưa được chọn. Lưu trong trình duyệt, tên học sinh không rời khỏi thiết bị.',
    },
    '/random-letter-wheel': {
      title: 'Vòng Quay Chữ Cái Ngẫu Nhiên — Chọn Chữ Miễn Phí',
      description: 'Quay vòng bảng chữ cái để chọn một chữ ngẫu nhiên. A–Z, chỉ nguyên âm hoặc phụ âm. Dành cho trò chơi chữ, Scrabble và hoạt động học tập.',
    },
    '/prize-wheel': {
      title: 'Vòng Quay Phần Thưởng — Bốc Thăm Online Miễn Phí',
      description: 'Tạo vòng quay phần thưởng miễn phí cho sự kiện và quà tặng. Thêm phần thưởng, quay để thắng và dùng tự động xóa cho các lần bốc thăm công bằng.',
    },
    '/decision-wheel': {
      title: 'Vòng Quay Quyết Định — Chọn Ngẫu Nhiên Miễn Phí',
      description: 'Không thể quyết định? Quay vòng quyết định và để nó chọn giúp bạn. Thêm các tùy chọn, quay và nhận câu trả lời ngay lập tức. Miễn phí, không cần đăng ký.',
    },
    '/trivia-wheel': {
      title: 'Vòng Quay Câu Đố — Chọn Danh Mục Miễn Phí',
      description: 'Quay vòng câu đố để chọn danh mục quiz ngẫu nhiên ngay lập tức. Trò chơi miễn phí cho đêm câu đố, lớp học và tiệc tùng. 10 danh mục, tùy chỉnh hoàn toàn.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Tạo Vòng Quay May Mắn — Miễn Phí Online',
      description: 'Tạo vòng quay may mắn của riêng bạn trực tuyến trong một phút — miễn phí, không cần đăng ký. Thêm phần thưởng hoặc danh mục, quay và chia sẻ liên kết.',
    },
    '/spin-the-bottle': {
      title: 'Quay Chai Online — Ứng Dụng Tiệc Miễn Phí',
      description: 'Ứng dụng quay chai miễn phí cho các bữa tiệc. Thêm tên người chơi và quay để chọn ngẫu nhiên. Hoàn hảo cho trò thật hay thách thức. Không cần tải xuống.',
    },
    '/number-wheel': {
      title: 'Vòng Quay Số — Tạo Số Ngẫu Nhiên Miễn Phí',
      description: 'Vòng quay số miễn phí — đặt bất kỳ phạm vi nào (1–10, 1–100 hoặc tùy chỉnh). Dành cho trò chơi toán học, bốc thăm và xúc xắc kỹ thuật số. Không cần đăng ký.',
    },
    '/random-team-generator': {
      title: 'Tạo Nhóm Ngẫu Nhiên — Công Cụ Miễn Phí',
      description: 'Chia bất kỳ nhóm nào thành các đội ngẫu nhiên công bằng trong vài giây. Công cụ miễn phí cho giáo viên, quản lý và game thủ. Không cần đăng ký.',
    },
  },

  th: {
    '/': {
      // <!-- SEO: Thai random-wheel landing metadata. -->
      title: 'วงล้อสุ่ม — เลือกชื่อแบบสุ่มฟรี | Name Wheel',
      description: 'หมุนวงล้อสุ่มเพื่อเลือกชื่อ แบ่งทีม และตัดสินใจ ใช้งานฟรีไม่ต้องสมัครสมาชิก',
    },
    '/classroom-name-picker': {
      title: 'สุ่มชื่อนักเรียน — เครื่องมือฟรีสำหรับครู',
      description: 'เครื่องมือสุ่มชื่อฟรีสำหรับครู เลือกนักเรียนอย่างยุติธรรมและสุ่ม เพิ่มรายชื่อชั้นเรียนและหมุนได้เลย',
    },
    '/giveaway-wheel': {
      title: 'วงล้อของรางวัล — เครื่องมือสุ่มผู้ชนะฟรี',
      description: 'จัดกิจกรรมชิงรางวัลที่ยุติธรรมและโปร่งใสด้วยวงล้อฟรีของเรา เพิ่มผู้เข้าร่วมและหมุนเพื่อเลือกผู้ชนะแบบสุ่ม',
    },
    '/yes-no-wheel': {
      title: 'วงล้อใช่หรือไม่ — เครื่องมือตัดสินใจสุ่มฟรี',
      description: 'ตัดสินใจไม่ได้? หมุนวงล้อใช่/ไม่ฟรีของเราเพื่อรับคำตอบแบบสุ่มทันที เหมาะสำหรับเกมและตัวเลือกในชีวิตประจำวัน',
    },
    '/gallery': {
      title: 'แกลเลอรีเทมเพลต — วงล้อสำเร็จรูป',
      description: 'เรียกดู 48 เทมเพลตวงล้อสำเร็จรูป โหลดวงล้อด้วยคลิกเดียว: วันต่างๆ ลูกเต๋า ใช่/ไม่ ประเภทภาพยนตร์ และอื่นๆ',
    },
    '/how-to': {
      title: 'วิธีใช้วงล้อสุ่ม — คู่มือทีละขั้นตอน',
      description: 'เรียนรู้วิธีใช้วงล้อ TheNameWheel เพิ่มชื่อ ปรับแต่งวงล้อ หมุน และแชร์ผลลัพธ์',
    },
    '/faq': {
      title: 'คำถามที่พบบ่อย — วงล้อ TheNameWheel',
      description: 'คำตอบสำหรับคำถามที่พบบ่อยเกี่ยวกับ TheNameWheel สุ่มจริงหรือเปล่า? ทำงานอย่างไร? บันทึกวงล้อได้ไหม?',
    },
    '/about': {
      title: 'เกี่ยวกับ TheNameWheel — เครื่องมือสุ่มชื่อฟรี',
      description: 'TheNameWheel คือเครื่องมือสุ่มชื่อฟรีและเป็นส่วนตัว สร้างมาสำหรับครู สตรีมเมอร์ และทุกคนที่ต้องการการเลือกที่ยุติธรรม',
    },
    '/contact': {
      title: 'ติดต่อเรา | TheNameWheel',
      description: 'มีคำถาม คำขอฟีเจอร์ หรือข้อเสนอแนะเกี่ยวกับ TheNameWheel? ส่งข้อความผ่านแบบฟอร์มติดต่อของเรา',
    },
    '/classes': {
      title: 'ชั้นเรียนของคุณ — วงล้อที่บันทึก | TheNameWheel',
      description: 'จัดการวงล้อชั้นเรียนที่บันทึกทั้งหมด: รายชื่อนักเรียน ประวัติการหมุน และใครยังไม่ได้รับเลือก เก็บไว้ในเบราว์เซอร์ ชื่อไม่ออกจากอุปกรณ์',
    },
    '/random-letter-wheel': {
      title: 'วงล้อตัวอักษรสุ่ม — เลือกตัวอักษรฟรี',
      description: 'หมุนวงล้ออักษรเพื่อเลือกตัวอักษรสุ่ม A–Z เฉพาะสระ หรือพยัญชนะ เหมาะสำหรับเกมคำศัพท์ สกรับเบิ้ล และกิจกรรมห้องเรียน',
    },
    '/prize-wheel': {
      title: 'วงล้อรางวัล — จับฉลากออนไลน์ฟรี',
      description: 'สร้างวงล้อรางวัลฟรีสำหรับกิจกรรมและของกำนัล เพิ่มรางวัล หมุนเพื่อชนะ ใช้การลบอัตโนมัติสำหรับการจับฉลากหลายรางวัลที่ยุติธรรม',
    },
    '/decision-wheel': {
      title: 'วงล้อตัดสินใจ — เลือกสุ่มฟรี',
      description: 'ตัดสินใจไม่ได้? หมุนวงล้อและให้มันเลือกให้คุณ เพิ่มตัวเลือก หมุน และรับคำตอบทันที เครื่องมือฟรี ไม่ต้องสมัครสมาชิก',
    },
    '/trivia-wheel': {
      title: 'วงล้อไตรเวีย — เลือกหมวดคำถามฟรี',
      description: 'หมุนวงล้อไตรเวียเพื่อเลือกหมวดหมู่คำถามแบบสุ่มทันที เกมฟรีสำหรับคืนปัญหาความรู้ ห้องเรียน และปาร์ตี้ 10 หมวดหมู่พร้อมปรับแต่ง',
    },
    '/wheel-of-fortune-maker': {
      title: 'สร้างวงล้อโชคชะตา — ฟรีออนไลน์',
      description: 'สร้างวงล้อโชคชะตาของคุณเองในหนึ่งนาที — ฟรี ไม่ต้องสมัคร เพิ่มรางวัลหรือหมวดหมู่ หมุนและแชร์ลิงก์หรือฝังในเว็บไซต์',
    },
    '/spin-the-bottle': {
      title: 'หมุนขวดออนไลน์ — เกมปาร์ตี้ฟรี',
      description: 'แอปหมุนขวดฟรีสำหรับงานปาร์ตี้ เพิ่มชื่อผู้เล่นและหมุนเพื่อเลือกแบบสุ่ม เหมาะสำหรับเกมจริงหรือท้าทาย ไม่ต้องดาวน์โหลด',
    },
    '/number-wheel': {
      title: 'วงล้อตัวเลข — สร้างตัวเลขสุ่มฟรี',
      description: 'วงล้อตัวเลขฟรี — กำหนดช่วงใดก็ได้ (1–10, 1–100 หรือกำหนดเอง) เหมาะสำหรับเกมคณิตศาสตร์ จับฉลาก และลูกเต๋าดิจิทัล ไม่ต้องสมัครสมาชิก',
    },
    '/random-team-generator': {
      title: 'สร้างทีมสุ่ม — เครื่องมือแบ่งกลุ่มฟรี',
      description: 'แบ่งกลุ่มใดก็ได้เป็นทีมสุ่มที่ยุติธรรมในไม่กี่วินาที เครื่องมือฟรีสำหรับครู ผู้จัดการ และนักเล่นเกม ไม่ต้องสมัครสมาชิก',
    },
  },

  sv: {
    '/': {
      title: 'Gratis Slumphjul och Namnväljare',
      description: 'Gratis slumphjul online — lägg till namn och snurra för att välja en slumpmässig vinnare. Perfekt för klassrum, tävlingar, utlottningar och beslut.',
    },
    '/classroom-name-picker': {
      title: 'Klassrumsnamnsväljare — Gratis Verktyg för Lärare',
      description: 'Gratis slumpmässig namnväljare för lärare. Välj elever rättvist och slumpmässigt. Lägg till din klasslista och snurra direkt.',
    },
    '/giveaway-wheel': {
      title: 'Utlottningshjul — Gratis Vinnarväljare',
      description: 'Gör en rättvis och transparent utlottning med vårt gratis hjul. Lägg till deltagare och snurra för att välja en slumpmässig vinnare.',
    },
    '/yes-no-wheel': {
      title: 'Ja eller Nej-hjul — Gratis Slumpmässigt Beslut',
      description: 'Kan du inte bestämma dig? Snurra vårt gratis Ja/Nej-hjul för ett omedelbart slumpsvar. Perfekt för spel och vardagliga val.',
    },
    '/gallery': {
      title: 'Mallgalleri — Färdiga Slumphjul',
      description: 'Bläddra bland 48 färdiga hjulmallar. Ladda ett hjul med ett klick: dagar, tärning, ja/nej, filmgenrer och mer.',
    },
    '/how-to': {
      title: 'Hur man Använder Slumphjulet — Steg för Steg',
      description: 'Lär dig använda TheNameWheel. Lägg till namn, anpassa hjulet, snurra och dela resultaten.',
    },
    '/faq': {
      title: 'Vanliga Frågor — TheNameWheel-hjulet',
      description: 'Svar på vanliga frågor om TheNameWheel. Är det verkligen slumpmässigt? Hur fungerar det? Kan jag spara mitt hjul?',
    },
    '/about': {
      title: 'Om TheNameWheel — Gratis Namnväljare',
      description: 'TheNameWheel är ett gratis och privat verktyg för slumpmässigt namnval, skapat för lärare, streamers och alla som behöver ett rättvist val.',
    },
    '/contact': {
      title: 'Kontakta Oss | TheNameWheel',
      description: 'Har du en fråga, önskemål om funktion eller feedback om TheNameWheel? Skicka ett meddelande via vårt kontaktformulär.',
    },
    '/classes': {
      title: 'Dina Klasser — Sparade Hjul | TheNameWheel',
      description: 'Hantera alla sparade klasshjul: elevlistor, snurrhistorik och vem som ännu inte valts. Lagrat i din webbläsare, elevnamn lämnar aldrig din enhet.',
    },
    '/random-letter-wheel': {
      title: 'Slumpmässigt Bokstavshjul — Gratis Alfabetsväljare',
      description: 'Snurra alfabetshjulet för att välja en slumpmässig bokstav. A–Ö, bara vokaler eller konsonanter. Perfekt för Scrabble, ordspel och klassrumsaktiviteter.',
    },
    '/prize-wheel': {
      title: 'Prishjul — Gratis Online Dragning',
      description: 'Skapa ett gratis prishjul för evenemang och dragningar. Lägg till priser, snurra för att vinna och använd automatisk borttagning för rättvisa dragningar.',
    },
    '/decision-wheel': {
      title: 'Beslutshjul — Gratis Slumpmässig Beslutshjälp',
      description: 'Kan du inte bestämma dig? Snurra beslutshjulet och låt det välja åt dig. Lägg till alternativ, snurra och få ett omedelbart svar. Gratis, ingen registrering.',
    },
    '/trivia-wheel': {
      title: 'Triviahjul — Gratis Quizkategoriväljarare',
      description: 'Snurra triviahjulet för att välja en quizkategori direkt. Gratis spel för pubquizzar, klasser och spelnätter. 10 kategorier laddade, fullt anpassningsbart.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Skapa Lyckohjul — Gratis Online',
      description: 'Skapa ditt eget lyckohjul online på en minut — gratis, ingen registrering. Lägg till priser eller kategorier, snurra och dela länken eller bädda in på din webbplats.',
    },
    '/spin-the-bottle': {
      title: 'Snurra Flaskan Online — Gratis Partyspel',
      description: 'Gratis onlinespel för att snurra flaskan på fester. Lägg till spelarnamn och snurra för att välja slumpmässigt. Perfekt för sanning eller konsekvens.',
    },
    '/number-wheel': {
      title: 'Sifferhjul — Gratis Slumpmässig Nummergenerator',
      description: 'Gratis sifferhjul — ange valfritt intervall (1–10, 1–100 eller anpassat). För mattespel, dragningar och digitala tärningar. Ingen registrering.',
    },
    '/random-team-generator': {
      title: 'Slumpmässig Lagindelning — Gratis Gruppverktyg',
      description: 'Dela upp vilken grupp som helst i rättvisa slumpmässiga lag på sekunder. Gratis för lärare, chefer och spelare. Ingen registrering krävs.',
    },
  },

  el: {
    '/': {
      title: 'Δωρεάν Τυχαία Ρόδα και Επιλογέας Ονομάτων',
      description: 'Δωρεάν τυχαία ρόδα — προσθέστε ονόματα και περιστρέψτε για να επιλέξετε τυχαίο νικητή. Ιδανικό για τάξεις, διαγωνισμούς, κληρώσεις και αποφάσεις.',
    },
    '/classroom-name-picker': {
      title: 'Τυχαία Επιλογή Μαθητή — Δωρεάν Εργαλείο',
      description: 'Δωρεάν τυχαία επιλογή ονόματος για εκπαιδευτικούς. Επιλέξτε μαθητές δίκαια και τυχαία. Προσθέστε τη λίστα της τάξης και γυρίστε αμέσως.',
    },
    '/giveaway-wheel': {
      title: 'Ρόδα Κλήρωσης — Δωρεάν Επιλογέας Νικητή',
      description: 'Κάντε μια δίκαιη κλήρωση με τη δωρεάν ρόδα μας. Προσθέστε συμμετέχοντες και περιστρέψτε για να επιλέξετε τυχαίο νικητή.',
    },
    '/yes-no-wheel': {
      title: 'Ρόδα Ναι ή Όχι — Τυχαίο Εργαλείο Απόφασης',
      description: 'Δεν μπορείτε να αποφασίσετε; Περιστρέψτε τη δωρεάν ρόδα Ναι/Όχι για άμεση τυχαία απάντηση. Ιδανικό για παιχνίδια και καθημερινές επιλογές.',
    },
    '/gallery': {
      title: 'Γκαλερί Προτύπων — Έτοιμες Τυχαίες Ρόδες',
      description: 'Εξερευνήστε 48 έτοιμα πρότυπα ρόδας. Φορτώστε μια ρόδα με ένα κλικ: ημέρες, ζάρι, ναι/όχι, είδη ταινιών και άλλα.',
    },
    '/how-to': {
      title: 'Πώς να Χρησιμοποιήσετε την Τυχαία Ρόδα',
      description: 'Μάθετε να χρησιμοποιείτε τη ρόδα TheNameWheel. Προσθέστε ονόματα, προσαρμόστε τη ρόδα, περιστρέψτε και μοιραστείτε τα αποτελέσματα.',
    },
    '/faq': {
      title: 'Συχνές Ερωτήσεις — Ρόδα TheNameWheel',
      description: 'Απαντήσεις σε συχνές ερωτήσεις για το TheNameWheel. Είναι πραγματικά τυχαίο; Πώς λειτουργεί; Μπορώ να αποθηκεύσω τη ρόδα μου;',
    },
    '/about': {
      title: 'Σχετικά με το TheNameWheel — Δωρεάν Επιλογέας',
      description: 'Το TheNameWheel είναι ένα δωρεάν, ιδιωτικό εργαλείο τυχαίας επιλογής ονομάτων, σχεδιασμένο για εκπαιδευτικούς, streamers και όποιον χρειάζεται δίκαιη επιλογή.',
    },
    '/contact': {
      title: 'Επικοινωνία | TheNameWheel',
      description: 'Έχετε ερώτηση, αίτημα λειτουργίας ή σχόλια για το TheNameWheel; Στείλτε μας μήνυμα μέσω της φόρμας επικοινωνίας.',
    },
    '/classes': {
      title: 'Τάξεις Σας — Αποθηκευμένοι Τροχοί | TheNameWheel',
      description: 'Διαχειριστείτε όλους τους αποθηκευμένους τροχούς τάξης: λίστες μαθητών, ιστορικό γυρισμάτων και ποιος δεν έχει επιλεγεί ακόμα. Αποθηκευμένο στον περιηγητή σας.',
    },
    '/random-letter-wheel': {
      title: 'Τυχαίος Τροχός Γραμμάτων — Δωρεάν Επιλογή',
      description: 'Γυρίστε τον αλφαβητικό τροχό για τυχαία επιλογή γράμματος. Α–Ω, μόνο φωνήεντα ή σύμφωνα. Ιδανικός για Scrabble, παιχνίδια λέξεων και σχολικές δραστηριότητες.',
    },
    '/prize-wheel': {
      title: 'Τροχός Βραβείων — Δωρεάν Online Κλήρωση',
      description: 'Δημιουργήστε δωρεάν τροχό βραβείων για εκδηλώσεις και κληρώσεις. Προσθέστε βραβεία, γυρίστε για να κερδίσετε και χρησιμοποιήστε αυτόματη αφαίρεση.',
    },
    '/decision-wheel': {
      title: 'Τροχός Αποφάσεων — Δωρεάν Τυχαίος Βοηθός',
      description: 'Δεν μπορείτε να αποφασίσετε; Γυρίστε τον τροχό και αφήστε τον να επιλέξει για εσάς. Προσθέστε επιλογές, γυρίστε και λάβετε άμεση απάντηση. Δωρεάν, χωρίς εγγραφή.',
    },
    '/trivia-wheel': {
      title: 'Τροχός Τριβιά — Δωρεάν Επιλογέας Κατηγορίας',
      description: 'Γυρίστε τον τροχό τριβιά για άμεση τυχαία επιλογή κατηγορίας κουίζ. Δωρεάν για βραδιές ερωτήσεων, τάξεις και πάρτι. 10 κατηγορίες, πλήρως προσαρμόσιμος.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Δημιουργός Τροχού Τύχης — Δωρεάν Online',
      description: 'Φτιάξτε τον δικό σας τροχό τύχης online σε ένα λεπτό — δωρεάν, χωρίς εγγραφή. Προσθέστε βραβεία ή κατηγορίες, γυρίστε και μοιραστείτε τον σύνδεσμο.',
    },
    '/spin-the-bottle': {
      title: 'Μπουκάλι Online — Δωρεάν Παιχνίδι Πάρτι',
      description: 'Δωρεάν παιχνίδι μπουκαλιού για πάρτι. Προσθέστε παίκτες και γυρίστε τη ρόδα για τυχαία επιλογή. Ιδανικό για αλήθεια ή τόλμη. Χωρίς λήψη.',
    },
    '/number-wheel': {
      title: 'Τροχός Αριθμών — Δωρεάν Τυχαία Γεννήτρια',
      description: 'Δωρεάν τροχός αριθμών — ορίστε οποιοδήποτε εύρος (1–10, 1–100 ή προσαρμοσμένο). Για παιχνίδια μαθηματικών, κληρώσεις και ψηφιακά ζάρια. Χωρίς εγγραφή.',
    },
    '/random-team-generator': {
      title: 'Τυχαία Δημιουργία Ομάδων — Δωρεάν Διαχωριστής',
      description: 'Χωρίστε οποιαδήποτε ομάδα σε τυχαίες δίκαιες ομάδες σε δευτερόλεπτα. Δωρεάν για εκπαιδευτικούς, διαχειριστές και παίκτες. Χωρίς εγγραφή.',
    },
  },

  cs: {
    '/': {
      title: 'Zdarma Náhodné Kolo a Výběr Jmen',
      description: 'Zdarma náhodné kolo online — přidejte jména a zatočte pro výběr náhodného vítěze. Skvělé pro třídu, soutěže, slosování a rozhodování.',
    },
    '/classroom-name-picker': {
      title: 'Náhodný Výběr Žáků — Zdarma pro Učitele',
      description: 'Zdarma náhodný výběr jmen pro učitele. Vybírejte žáky spravedlivě a náhodně. Přidejte seznam třídy a ihned zatočte.',
    },
    '/giveaway-wheel': {
      title: 'Slosovací Kolo — Zdarma Výběr Výherce',
      description: 'Proveďte spravedlivé a transparentní slosování s naším zdarma kolem. Přidejte účastníky a zatočte pro výběr náhodného výherce.',
    },
    '/yes-no-wheel': {
      title: 'Kolo Ano nebo Ne — Zdarma Náhodné Rozhodování',
      description: 'Nemůžete se rozhodnout? Zatočte naším zdarma kolem Ano/Ne pro okamžitou náhodnou odpověď. Ideální pro hry a každodenní volby.',
    },
    '/gallery': {
      title: 'Galerie Šablon — Hotová Náhodná Kola',
      description: 'Prohlédněte si 48 hotových šablon kola. Načtěte kolo jedním klikem: dny, kostka, ano/ne, filmové žánry a další.',
    },
    '/how-to': {
      title: 'Jak Používat Náhodné Kolo — Průvodce',
      description: 'Naučte se používat kolo TheNameWheel. Přidejte jména, přizpůsobte kolo, zatočte a sdílejte výsledky.',
    },
    '/faq': {
      title: 'Časté Dotazy — Kolo TheNameWheel',
      description: 'Odpovědi na časté dotazy o TheNameWheel. Je to opravdu náhodné? Jak to funguje? Mohu uložit své kolo?',
    },
    '/about': {
      title: 'O TheNameWheel — Zdarma Výběr Jmen',
      description: 'TheNameWheel je zdarma a soukromý nástroj pro náhodný výběr jmen, vytvořený pro učitele, streamery a každého, kdo potřebuje spravedlivý výběr.',
    },
    '/contact': {
      title: 'Kontaktujte Nás | TheNameWheel',
      description: 'Máte otázku, požadavek na funkci nebo zpětnou vazbu k TheNameWheel? Pošlete nám zprávu prostřednictvím kontaktního formuláře.',
    },
    '/classes': {
      title: 'Vaše Třídy — Uložená Kola | TheNameWheel',
      description: 'Spravujte všechna uložená třídní kola: seznamy žáků, historii otáčení a kdo ještě nebyl vybrán. Uloženo v prohlížeči, jména žáků nikdy neopustí vaše zařízení.',
    },
    '/random-letter-wheel': {
      title: 'Kolo Náhodných Písmen — Zdarma Výběr Abecedy',
      description: 'Otočte abecedním kolem pro výběr náhodného písmene. A–Z, pouze samohlásky nebo souhlásky. Ideální pro Scrabble, slovní hry a školní aktivity.',
    },
    '/prize-wheel': {
      title: 'Kolo Cen — Zdarma Online Slosování',
      description: 'Vytvořte zdarma kolo cen pro akce a slosování. Přidejte ceny, otočte pro výhru a použijte automatické odebírání pro spravedlivá slosování více cen.',
    },
    '/decision-wheel': {
      title: 'Rozhodovací Kolo — Zdarma Náhodný Pomocník',
      description: 'Nemůžete se rozhodnout? Otočte kolečkem a nechte ho vybrat za vás. Přidejte možnosti, otočte a získejte okamžitou odpověď. Zdarma, bez registrace.',
    },
    '/trivia-wheel': {
      title: 'Trivia Kolo — Zdarma Výběr Kategorie Kvízu',
      description: 'Otočte trivia kolem pro okamžitý výběr kategorie kvízu. Zdarma pro kvízové večery, třídy a párty. 10 kategorií, plně přizpůsobitelné.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Vytvořit Kolo Štěstí — Zdarma Online',
      description: 'Vytvořte vlastní kolo štěstí online za minutu — zdarma, bez registrace. Přidejte ceny nebo kategorie, otočte a sdílejte odkaz nebo vložte na web.',
    },
    '/spin-the-bottle': {
      title: 'Točení Lahví Online — Zdarma Párty Hra',
      description: 'Zdarma online hra točení lahví pro párty. Přidejte hráče a otočte kolečko pro náhodný výběr. Skvělé pro pravdu nebo výzvu. Bez stahování.',
    },
    '/number-wheel': {
      title: 'Číselné Kolo — Zdarma Náhodný Generátor Čísel',
      description: 'Zdarma číselné kolo — nastavte libovolný rozsah (1–10, 1–100 nebo vlastní). Pro matematické hry, slosování a digitální kostku. Bez registrace.',
    },
    '/random-team-generator': {
      title: 'Generátor Náhodných Týmů — Zdarma',
      description: 'Rozdělte jakoukoli skupinu na spravedlivé náhodné týmy během sekund. Zdarma pro učitele, manažery a hráče. Bez registrace.',
    },
  },

  da: {
    '/': {
      title: 'Gratis Tilfældig Hjul og Navnevælger',
      description: 'Gratis tilfældig hjul online — tilføj navne og spin for at vælge en tilfældig vinder. Perfekt til klasserum, konkurrencer, lodtrækning og beslutninger.',
    },
    '/classroom-name-picker': {
      title: 'Klassens Navnevælger — Gratis Værktøj til Lærere',
      description: 'Gratis tilfældig navnevælger til lærere. Vælg elever retfærdigt og tilfældigt. Tilføj din klasserliste og spin med det samme.',
    },
    '/giveaway-wheel': {
      title: 'Lodtrækningshjul — Gratis Vindervælger',
      description: 'Lav en retfærdig og gennemsigtig lodtrækning med vores gratis hjul. Tilføj deltagere og spin for at vælge en tilfældig vinder.',
    },
    '/yes-no-wheel': {
      title: 'Ja eller Nej-hjul — Gratis Tilfældig Beslutning',
      description: 'Kan du ikke beslutte dig? Spin vores gratis Ja/Nej-hjul for et øjeblikkeligt tilfældigt svar. Ideel til spil og hverdagsvalg.',
    },
    '/gallery': {
      title: 'Skabelongalleri — Færdige Tilfældige Hjul',
      description: 'Gennemse 48 færdige hjulskabeloner. Indlæs et hjul med ét klik: dage, terning, ja/nej, filmgenrer og mere.',
    },
    '/how-to': {
      title: 'Sådan Bruger Du Det Tilfældige Hjul — Guide',
      description: 'Lær at bruge TheNameWheel-hjulet. Tilføj navne, tilpas hjulet, spin og del resultaterne.',
    },
    '/faq': {
      title: 'Ofte Stillede Spørgsmål — TheNameWheel-hjulet',
      description: 'Svar på ofte stillede spørgsmål om TheNameWheel. Er det virkelig tilfældigt? Hvordan virker det? Kan jeg gemme mit hjul?',
    },
    '/about': {
      title: 'Om TheNameWheel — Gratis Navnevælger',
      description: 'TheNameWheel er et gratis og privat værktøj til tilfældig navneplukning, skabt til lærere, streamers og alle der har brug for et retfærdigt valg.',
    },
    '/contact': {
      title: 'Kontakt Os | TheNameWheel',
      description: 'Har du et spørgsmål, en funktionsanmodning eller feedback om TheNameWheel? Send os en besked via vores kontaktformular.',
    },
    '/classes': {
      title: 'Dine Klasser — Gemte Hjul | TheNameWheel',
      description: 'Administrer alle dine gemte klassehjul: elevlister, drejehistorik og hvem der endnu ikke er valgt. Gemt i din browser, elevnavne forlader aldrig din enhed.',
    },
    '/random-letter-wheel': {
      title: 'Tilfældig Bogstavhjul — Gratis Alfabetvælger',
      description: 'Spin alfabethjulet for at vælge et tilfældigt bogstav. A–Z, kun vokaler eller konsonanter. Perfekt til Scrabble, ordspil og klasseaktiviteter.',
    },
    '/prize-wheel': {
      title: 'Præmiehjul — Gratis Online Lodtrækning',
      description: 'Lav et gratis præmiehjul til events og konkurrencer. Tilføj præmier, spin for at vinde og brug automatisk fjernelse til retfærdig multi-præmie lodtrækning.',
    },
    '/decision-wheel': {
      title: 'Beslutningshjul — Gratis Tilfældig Beslutningshjælp',
      description: 'Kan du ikke beslutte dig? Spin beslutningshjulet og lad det vælge for dig. Tilføj muligheder, spin og få et øjeblikkeligt svar. Gratis, ingen tilmelding.',
    },
    '/trivia-wheel': {
      title: 'Trivia Hjul — Gratis Quizkategori Vælger',
      description: 'Spin triviahjulet for øjeblikkeligt at vælge en quizkategori. Gratis spil til pubquizzer, klasser og fester. 10 kategorier loaded, fuldt tilpasningsdygtigt.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Lykkehjul Skaber — Gratis Online',
      description: 'Lav dit eget lykkehjul online på et minut — gratis, ingen tilmelding. Tilføj præmier eller kategorier, spin og del linket eller integrer på din hjemmeside.',
    },
    '/spin-the-bottle': {
      title: 'Drejeflasken Online — Gratis Partyspil App',
      description: 'Gratis online drejeflasken til fester. Tilføj spillernavne og spin for tilfældig udvælgelse. Perfekt til sandhed eller konsekvens. Ingen download nødvendig.',
    },
    '/number-wheel': {
      title: 'Talhjul — Gratis Tilfældig Talgenerator',
      description: 'Gratis talhjul — indstil ethvert interval (1–10, 1–100 eller brugerdefineret). Til matematikspil, lodtrækning og digitale terninger. Ingen tilmelding.',
    },
    '/random-team-generator': {
      title: 'Tilfældig Holdgenerator — Gratis Gruppefordeler',
      description: 'Del enhver gruppe i retfærdige tilfældige hold på sekunder. Gratis til lærere, ledere og spillere. Ingen tilmelding krævet.',
    },
  },

  fi: {
    '/': {
      title: 'Ilmainen Satunnainen Pyörä ja Nimienvalitsin',
      description: 'Ilmainen satunnainen pyörä verkossa — lisää nimiä ja pyöräytä valitaksesi satunnaisen voittajan. Täydellinen luokkahuoneisiin, arvontoihin ja päätöksiin.',
    },
    '/classroom-name-picker': {
      title: 'Luokan Nimenvalitsin — Ilmainen Opettajille',
      description: 'Ilmainen satunnainen nimenvalitsin opettajille. Valitse oppilaita tasapuolisesti ja satunnaisesti. Lisää luokkalista ja pyöräytä heti.',
    },
    '/giveaway-wheel': {
      title: 'Arvontapyörä — Ilmainen Voittajanvalitsin',
      description: 'Järjestä reilu ja läpinäkyvä arvonta ilmaisella pyörällämme. Lisää osallistujat ja pyöräytä valitaksesi satunnaisen voittajan.',
    },
    '/yes-no-wheel': {
      title: 'Kyllä vai Ei -pyörä — Ilmainen Satunnainen Päätös',
      description: 'Et pysty päättämään? Pyöräytä ilmaista Kyllä/Ei-pyöräämme saadaksesi välittömän satunnaisen vastauksen. Ihanteellinen peleihin ja arkipäivän valintoihin.',
    },
    '/gallery': {
      title: 'Malligalleria — Valmiit Satunnaiset Pyörät',
      description: 'Selaa 48 valmista pyörämallia. Lataa pyörä yhdellä klikkauksella: päivät, noppa, kyllä/ei, elokuvalajit ja paljon muuta.',
    },
    '/how-to': {
      title: 'Kuinka Käyttää Satunnaista Pyörää — Opas',
      description: 'Opi käyttämään TheNameWheel-pyörää. Lisää nimiä, mukauta pyörää, pyöräytä ja jaa tulokset.',
    },
    '/faq': {
      title: 'Usein Kysytyt Kysymykset — TheNameWheel-pyörä',
      description: 'Vastauksia usein kysyttyihin kysymyksiin TheNameWheelista. Onko se todella satunnainen? Miten se toimii? Voinko tallentaa pyöräni?',
    },
    '/about': {
      title: 'Tietoa TheNameWheelista — Ilmainen Nimenvalitsin',
      description: 'TheNameWheel on ilmainen ja yksityinen satunnainen nimenvalitsin, luotu opettajille, striimaajille ja kaikille, jotka tarvitsevat reilun valinnan.',
    },
    '/contact': {
      title: 'Ota Yhteyttä | TheNameWheel',
      description: 'Onko sinulla kysymys, ominaisuuspyyntö tai palautetta TheNameWheelista? Lähetä viesti yhteydenottolomakkeemme kautta.',
    },
    '/classes': {
      title: 'Luokkasi — Tallennetut Pyörät | TheNameWheel',
      description: 'Hallinnoi kaikkia tallennettuja luokkapyöriä: oppilaslistat, pyörityshistoria ja keitä ei ole vielä valittu. Tallennettu selaimessasi, nimet eivät koskaan poistu laitteeltasi.',
    },
    '/random-letter-wheel': {
      title: 'Satunnainen Kirjainpyörä — Ilmainen Aakkosvalitsin',
      description: 'Pyöräytä aakkospyörää valitaksesi satunnaisen kirjaimen. A–Ö, vain vokaalit tai konsonantit. Sopii Scrabbleen, sanaleikkeihin ja luokkahuoneaktiviteetteihin.',
    },
    '/prize-wheel': {
      title: 'Palkintopyörä — Ilmainen Online-Arvonta',
      description: 'Luo ilmainen palkintopyörä tapahtumiin ja arvontoihin. Lisää palkintoja, pyöräytä voittaaksesi ja käytä automaattista poistoa reiluihin useamman palkinnon arvontoihin.',
    },
    '/decision-wheel': {
      title: 'Päätöspyörä — Ilmainen Satunnainen Päätösapu',
      description: 'Etkö pysty päättämään? Pyöräytä päätöspyörää ja anna sen valita puolestasi. Lisää vaihtoehtoja, pyöräytä ja saat välittömän vastauksen. Ilmainen, ei rekisteröintiä.',
    },
    '/trivia-wheel': {
      title: 'Tietovisa-Pyörä — Ilmainen Kategoriavalitsin',
      description: 'Pyöräytä tietovisa-pyörää valitaksesi satunnaisen visailukategorian välittömästi. Ilmainen peli tietokilpailuiltoihin, luokkiin ja juhliin. 10 kategoriaa, täysin muokattavissa.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Luo Onnenpyörä — Ilmainen Online',
      description: 'Luo oma onnenpyöräsi verkossa minuutissa — ilmaiseksi, ilman rekisteröitymistä. Lisää palkintoja tai kategorioita, pyöräytä ja jaa linkki tai upota sivustollesi.',
    },
    '/spin-the-bottle': {
      title: 'Pyöritä Pulloa Online — Ilmainen Juhlasovellus',
      description: 'Ilmainen pullonpyörityspeli juhliin. Lisää pelaajien nimet ja pyöräytä satunnaiseen valintaan. Erinomainen totuus vai tehtävä -peliin. Ei latausta.',
    },
    '/number-wheel': {
      title: 'Numeropyörä — Ilmainen Satunnaislukugeneraattori',
      description: 'Ilmainen numeropyörä — aseta mikä tahansa alue (1–10, 1–100 tai mukautettu). Matematiikkapelejä, arvontoja ja digitaalisia noppoja varten. Ei rekisteröintiä.',
    },
    '/random-team-generator': {
      title: 'Satunnainen Tiimijako — Ilmainen Ryhmänjakaja',
      description: 'Jaa mikä tahansa ryhmä reiluihin satunnaistiimeihin sekunneissa. Ilmainen työkalu opettajille, johtajille ja pelaajille. Ei rekisteröintiä.',
    },
  },

  hu: {
    '/': {
      title: 'Ingyenes Véletlen Kerék és Névválasztó',
      description: 'Ingyenes véletlen kerék: adjon hozzá neveket, majd pörgessen egy véletlen eredményért. Használható osztályteremben, nyereményjátékokban és egyszerű döntésekhez.',
    },
    '/classroom-name-picker': {
      title: 'Osztálytermi Névválasztó — Ingyenes Tanároknak',
      description: 'Ingyenes véletlenszerű névválasztó tanároknak. Válasszon tanulókat igazságosan és véletlenszerűen. Adja hozzá az osztálylistát és pörgessen azonnal.',
    },
    '/giveaway-wheel': {
      title: 'Sorsolási Kerék — Ingyenes Nyertes Választó',
      description: 'Tartson igazságos és átlátható sorsolást ingyenes kerékünkkel. Adja hozzá a résztvevőket és pörgessen véletlenszerű nyertes kiválasztásához.',
    },
    '/yes-no-wheel': {
      title: 'Igen vagy Nem Kerék — Véletlen Döntés',
      description: 'Nem tud dönteni? Pörgesse ingyenes Igen/Nem kerékünket azonnali véletlenszerű válaszért. Ideális játékokhoz és mindennapi döntésekhez.',
    },
    '/gallery': {
      title: 'Sablongaléria — Kész Véletlenszerű Kerekek',
      description: 'Böngésszen 48 kész kerék sablon között. Töltse be a kereket egy kattintással: napok, kocka, igen/nem, filmműfajok és még sok más.',
    },
    '/how-to': {
      title: 'A Véletlenszerű Kerék Használata — Útmutató',
      description: 'Tanulja meg a TheNameWheel kerék használatát. Adjon hozzá neveket, testreszabhatja a kereket, pörgessen és ossza meg az eredményeket.',
    },
    '/faq': {
      title: 'Gyakran Ismételt Kérdések — TheNameWheel',
      description: 'Válaszok a TheNameWheellel kapcsolatos kérdésekre. Valóban véletlenszerű? Hogyan működik? Elmenthetem a kerekemet?',
    },
    '/about': {
      title: 'A TheNameWheelről — Ingyenes Névválasztó',
      description: 'A TheNameWheel egy ingyenes és privát véletlenszerű névválasztó eszköz, amelyet tanároknak, streamereknek és mindenkinek készítettünk, akinek igazságos választásra van szüksége.',
    },
    '/contact': {
      title: 'Kapcsolat | TheNameWheel',
      description: 'Van kérdése, funkciókérelme vagy visszajelzése a TheNameWheelről? Küldjön üzenetet kapcsolatfelvételi űrlapunkon keresztül.',
    },
    '/classes': {
      title: 'Az Ön Osztályai — Mentett Kerekek | TheNameWheel',
      description: 'Kezelje az összes mentett osztálykereket: tanulói listák, pörgetési előzmények és ki nem lett még kiválasztva. A böngészőjében tárolva, adatok nem töltődnek fel.',
    },
    '/random-letter-wheel': {
      title: 'Véletlenszerű Betűkerék — Ingyenes Ábécéválasztó',
      description: 'Pörgesse az ábécékereket véletlenszerű betű kiválasztásához. A–Z, csak magánhangzók vagy mássalhangzók. Ideális Scrabble-hez, szójátékokhoz és osztálytermekhez.',
    },
    '/prize-wheel': {
      title: 'Nyereménykerék — Ingyenes Online Sorsolás',
      description: 'Készítsen ingyenes nyereménykereket eseményekhez és sorsolásokhoz. Adjon hozzá nyereményeket, pörgessen a nyerésért és használja az automatikus eltávolítást.',
    },
    '/decision-wheel': {
      title: 'Döntési Kerék — Ingyenes Véletlenszerű Döntéshozó',
      description: 'Nem tud dönteni? Pörgesse a döntési kereket és hagyja, hogy helyette válasszon. Adja hozzá a lehetőségeket, pörgessen és kapjon azonnali választ. Ingyenes.',
    },
    '/trivia-wheel': {
      title: 'Trivia Kerék — Ingyenes Kvízkategória-Választó',
      description: 'Pörgesse a trivia kereket a kvízkategória azonnali véletlenszerű kiválasztásához. Ingyenes kvízestékre, osztályokba és bulikba. 10 kategória betöltve, teljesen testreszabható.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Szerencsekerék Készítő — Ingyenes Online',
      description: 'Készítse el saját szerencsekerékét egy perc alatt — ingyenes, regisztráció nélkül. Adjon hozzá díjakat vagy kategóriákat, pörgessen és ossza meg a linket.',
    },
    '/spin-the-bottle': {
      title: 'Üvegcsavar Online — Ingyenes Partijáték',
      description: 'Ingyenes online üvegcsavar bulikhoz. Adja hozzá a játékosok nevét, pörgessen és válasszon véletlenszerűen. Ideális az igazság vagy merészség játékhoz.',
    },
    '/number-wheel': {
      title: 'Számkerék — Ingyenes Véletlenszerű Számgenerátor',
      description: 'Ingyenes számkerék — tetszőleges tartomány beállítása (1–10, 1–100 vagy egyéni). Matematikai játékokhoz, sorsolásokhoz és digitális kockához. Regisztráció nélkül.',
    },
    '/random-team-generator': {
      title: 'Véletlenszerű Csapatok Generátora — Ingyenes',
      description: 'Osszon fel bármilyen csoportot véletlenszerű, igazságos csapatokra másodpercek alatt. Ingyenes tanároknak, menedzsereknek és játékosoknak. Regisztráció nélkül.',
    },
  },

  ro: {
    '/': {
      title: 'Roată Aleatorie Gratuită și Selector de Nume',
      description: 'Roată aleatorie gratuită online — adăugați nume și rotiți pentru a alege un câștigător aleatoriu. Perfectă pentru clase, concursuri, tombole și decizii.',
    },
    '/classroom-name-picker': {
      title: 'Selector Aleatoriu de Elevi',
      description: 'Selector de nume gratuit pentru profesori. Alegeți elevi corect și aleatoriu. Adăugați lista clasei și rotiți imediat.',
    },
    '/giveaway-wheel': {
      title: 'Roată de Tombolă și Alegere Câștigător',
      description: 'Organizați un concurs corect și transparent cu roata noastră gratuită. Adăugați participanți și rotiți pentru a alege un câștigător aleatoriu.',
    },
    '/yes-no-wheel': {
      title: 'Roată Da sau Nu — Decizie Aleatorie Gratuită',
      description: 'Nu puteți decide? Rotiți roata noastră gratuită Da/Nu pentru un răspuns aleatoriu instantaneu. Ideal pentru jocuri și alegeri de zi cu zi.',
    },
    '/gallery': {
      title: 'Galerie de Șabloane — Roți Gata de Utilizare',
      description: 'Explorați 48 de șabloane de roată gata de utilizare. Încărcați o roată cu un singur clic: zile, zar, da/nu, genuri de film și altele.',
    },
    '/how-to': {
      title: 'Cum să Folosiți Roata Aleatorie — Ghid',
      description: 'Aflați cum să folosiți roata TheNameWheel. Adăugați nume, personalizați roata, rotiți și partajați rezultatele.',
    },
    '/faq': {
      title: 'Întrebări Frecvente — Roata TheNameWheel',
      description: 'Răspunsuri la întrebările frecvente despre TheNameWheel. Este cu adevărat aleatoriu? Cum funcționează? Pot salva roata mea?',
    },
    '/about': {
      title: 'Despre TheNameWheel — Selector Gratuit de Nume',
      description: 'TheNameWheel este un instrument gratuit și privat de selectare aleatorie a numelor, creat pentru profesori, streameri și oricine are nevoie de o alegere echitabilă.',
    },
    '/contact': {
      title: 'Contact | TheNameWheel',
      description: 'Aveți o întrebare, cerere de funcție sau feedback despre TheNameWheel? Trimiteți-ne un mesaj prin formularul de contact.',
    },
    '/classes': {
      title: 'Clasele Dvs. — Roți Salvate | TheNameWheel',
      description: 'Gestionați toate roțile de clasă salvate: liste de elevi, istoricul rotirilor și cine nu a fost ales încă. Salvat în browserul dvs., datele nu sunt niciodată încărcate.',
    },
    '/random-letter-wheel': {
      title: 'Roată Litere Aleatorii — Selector Alfabet Gratuit',
      description: 'Rotiți roata alfabetului pentru selectarea unei litere aleatorii. A–Z, numai vocale sau consoane. Perfectă pentru Scrabble, jocuri de cuvinte și activități școlare.',
    },
    '/prize-wheel': {
      title: 'Roată de Premii — Tombolă Online Gratuită',
      description: 'Creați gratuit o roată de premii pentru evenimente și tombole. Adăugați premii, rotiți pentru a câștiga și folosiți eliminarea automată pentru tombole multiple.',
    },
    '/decision-wheel': {
      title: 'Roată de Decizie — Ajutor Aleatoriu Gratuit',
      description: 'Nu puteți decide? Rotiți roata și lăsați-o să aleagă pentru dvs. Adăugați opțiuni, rotiți și primiți un răspuns instant. Gratuit, fără înregistrare.',
    },
    '/trivia-wheel': {
      title: 'Roată Trivia — Selector de Categorii Quiz Gratuit',
      description: 'Rotiți roata trivia pentru selectarea instantanee a unei categorii de quiz. Joc gratuit pentru seri de întrebări, clase și petreceri. 10 categorii, complet personalizabil.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Creator Roată a Norocului — Gratuit Online',
      description: 'Creați propria roată a norocului online în un minut — gratuit, fără înregistrare. Adăugați premii sau categorii, rotiți și partajați linkul sau încorporați pe site.',
    },
    '/spin-the-bottle': {
      title: 'Sticlă Rotitoare Online — Aplicație Petrecere Gratuită',
      description: 'Aplicație gratuită de sticlă rotitoare pentru petreceri. Adăugați jucători și rotiți roata pentru selecție aleatorie. Ideal pentru adevăr sau provocare.',
    },
    '/number-wheel': {
      title: 'Roată de Numere — Generator Aleatoriu Gratuit',
      description: 'Roată de numere gratuită — setați orice interval (1–10, 1–100 sau personalizat). Pentru jocuri matematice, trageri la sorți și zaruri digitale. Fără înregistrare.',
    },
    '/random-team-generator': {
      title: 'Generator de Echipe Aleatorii — Gratuit',
      description: 'Împărțiți orice grup în echipe aleatorii echitabile în câteva secunde. Instrument gratuit pentru profesori, manageri și jucători. Fără înregistrare.',
    },
  },

  sk: {
    '/': {
      title: 'Zadarmo Náhodné Koleso a Výber Mien',
      description: 'Zadarmo náhodné koleso online — pridajte mená a zatočte pre výber náhodného víťaza. Skvelé pre triedu, súťaže, žrebovanie a rozhodovanie.',
    },
    '/classroom-name-picker': {
      title: 'Náhodný Výber Žiakov — Zadarmo pre Učiteľov',
      description: 'Zadarmo náhodný výber mien pre učiteľov. Vyberajte žiakov spravodlivo a náhodne. Pridajte zoznam triedy a ihneď zatočte.',
    },
    '/giveaway-wheel': {
      title: 'Koleso Žrebovania — Zadarmo Výber Víťaza',
      description: 'Urobte spravodlivé a transparentné žrebovanie s naším zadarmo kolom. Pridajte účastníkov a zatočte pre výber náhodného víťaza.',
    },
    '/yes-no-wheel': {
      title: 'Koleso Áno alebo Nie — Náhodné Rozhodnutie',
      description: 'Nemôžete sa rozhodnúť? Zatočte naším zadarmo kolom Áno/Nie pre okamžitú náhodnú odpoveď. Ideálne pre hry a každodenné voľby.',
    },
    '/gallery': {
      title: 'Galéria Šablón — Hotové Náhodné Kolá',
      description: 'Pozrite si 48 hotových šablón kolesa. Načítajte koleso jedným kliknutím: dni, kocka, áno/nie, filmové žánre a ďalšie.',
    },
    '/how-to': {
      title: 'Ako Používať Náhodné Koleso — Sprievodca',
      description: 'Naučte sa používať koleso TheNameWheel. Pridajte mená, prispôsobte koleso, zatočte a zdieľajte výsledky.',
    },
    '/faq': {
      title: 'Často Kladené Otázky — Koleso TheNameWheel',
      description: 'Odpovede na často kladené otázky o TheNameWheel. Je to naozaj náhodné? Ako to funguje? Môžem uložiť svoje koleso?',
    },
    '/about': {
      title: 'O TheNameWheel — Zadarmo Výber Mien',
      description: 'TheNameWheel je zadarmo a súkromný nástroj na náhodný výber mien, vytvorený pre učiteľov, streamerov a každého, kto potrebuje spravodlivý výber.',
    },
    '/contact': {
      title: 'Kontaktujte Nás | TheNameWheel',
      description: 'Máte otázku, požiadavku na funkciu alebo spätnú väzbu k TheNameWheel? Pošlite nám správu cez kontaktný formulár.',
    },
    '/classes': {
      title: 'Vaše Triedy — Uložené Kolá | TheNameWheel',
      description: 'Spravujte všetky uložené triedne kolá: zoznamy žiakov, históriu otáčania a kto ešte nebol vybraný. Uložené v prehliadači, mená žiakov nikdy neopustia vaše zariadenie.',
    },
    '/random-letter-wheel': {
      title: 'Náhodné Koleso Písmen — Zadarmo Výber Abecedy',
      description: 'Otočte abecedným kolesom pre výber náhodného písmena. A–Z, len samohlásky alebo spoluhlásky. Ideálne pre Scrabble, slovné hry a školské aktivity.',
    },
    '/prize-wheel': {
      title: 'Koleso Cien — Zadarmo Online Žrebovanie',
      description: 'Vytvorte zadarmo koleso cien pre akcie a žrebovanie. Pridajte ceny, otočte pre výhru a použite automatické odstraňovanie pre spravodlivé žrebovania.',
    },
    '/decision-wheel': {
      title: 'Rozhodovacia Koleso — Zadarmo Náhodný Pomocník',
      description: 'Nemôžete sa rozhodnúť? Otočte kolesom a nechajte ho vybrať za vás. Pridajte možnosti, otočte a získajte okamžitú odpoveď. Zadarmo, bez registrácie.',
    },
    '/trivia-wheel': {
      title: 'Trivia Koleso — Zadarmo Výber Kategórie Kvízu',
      description: 'Otočte trivia kolesom pre okamžitý výber kategórie kvízu. Zadarmo pre kvízové večery, triedy a párty. 10 kategórií, plne prispôsobiteľné.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Tvorca Kolesa Šťastia — Zadarmo Online',
      description: 'Vytvorte vlastné koleso šťastia online za minútu — zadarmo, bez registrácie. Pridajte ceny alebo kategórie, otočte a zdieľajte odkaz alebo vložte na web.',
    },
    '/spin-the-bottle': {
      title: 'Točenie Fľaše Online — Zadarmo Párty Hra',
      description: 'Zadarmo online hra točenie fľaše pre párty. Pridajte hráčov a otočte koleso pre náhodný výber. Skvelé pre pravdu alebo výzvu. Bez sťahovania.',
    },
    '/number-wheel': {
      title: 'Číselné Koleso — Zadarmo Náhodný Generátor Čísel',
      description: 'Zadarmo číselné koleso — nastavte ľubovoľný rozsah (1–10, 1–100 alebo vlastný). Pre matematické hry, žrebovanie a digitálnu kocku. Bez registrácie.',
    },
    '/random-team-generator': {
      title: 'Generátor Náhodných Tímov — Zadarmo',
      description: 'Rozdeľte ľubovoľnú skupinu na spravodlivé náhodné tímy za sekundy. Zadarmo pre učiteľov, manažérov a hráčov. Bez registrácie.',
    },
  },

  bg: {
    '/': {
      title: 'Безплатно Случайно Колело и Избор на Имена',
      description: 'Безплатно случайно колело онлайн — добавете имена и завъртете за избор на случаен победител. Перфектно за класове, раздавания, томболи и решения.',
    },
    '/classroom-name-picker': {
      title: 'Случаен Избор на Ученик — Безплатно за Учители',
      description: 'Безплатен случаен избор на имена за учители. Избирайте ученици справедливо и случайно. Добавете списъка на класа и завъртете веднага.',
    },
    '/giveaway-wheel': {
      title: 'Колело за Томбола — Безплатен Избор на Победител',
      description: 'Проведете справедлива и прозрачна томбола с нашето безплатно колело. Добавете участниците и завъртете за случаен победител.',
    },
    '/yes-no-wheel': {
      title: 'Колело Да или Не — Безплатно Случайно Решение',
      description: 'Не можете да решите? Завъртете безплатното ни колело Да/Не за моментален случаен отговор. Идеално за игри и ежедневни избори.',
    },
    '/gallery': {
      title: 'Галерия с Шаблони — Готови Случайни Колела',
      description: 'Разгледайте 48 готови шаблона за колело. Заредете колело с едно кликване: дни, зар, да/не, филмови жанрове и още.',
    },
    '/how-to': {
      title: 'Как да Използвате Случайното Колело — Ръководство',
      description: 'Научете се да използвате колелото TheNameWheel. Добавете имена, персонализирайте, завъртете и споделете резултатите.',
    },
    '/faq': {
      title: 'Често Задавани Въпроси — Колело TheNameWheel',
      description: 'Отговори на чести въпроси за TheNameWheel. Наистина ли е случайно? Как работи? Мога ли да запазя колелото си?',
    },
    '/about': {
      title: 'За TheNameWheel — Безплатен Избор на Имена',
      description: 'TheNameWheel е безплатен и частен инструмент за случаен избор на имена, създаден за учители, стриймъри и всеки, кой се нуждае от справедлив избор.',
    },
    '/contact': {
      title: 'Свържете се с Нас | TheNameWheel',
      description: 'Имате въпрос, заявка за функция или обратна връзка за TheNameWheel? Изпратете ни съобщение чрез нашата форма за контакт.',
    },
    '/classes': {
      title: 'Вашите Класове — Запазени Колела | TheNameWheel',
      description: 'Управлявайте всички запазени класни колела: списъци с ученици, история на завъртанията и кой все още не е избран. Съхранено в браузъра ви.',
    },
    '/random-letter-wheel': {
      title: 'Случайно Колело на Букви — Безплатен Избор',
      description: 'Завъртете азбучното колело за избор на случайна буква. А–Я, само гласни или съгласни. Идеално за Scrabble, словесни игри и класни дейности.',
    },
    '/prize-wheel': {
      title: 'Колело на Наградите — Безплатна Онлайн Томбола',
      description: 'Създайте безплатно колело на наградите за събития и томболи. Добавете награди, завъртете за печалба и използвайте автоматично премахване.',
    },
    '/decision-wheel': {
      title: 'Колело на Решенията — Безплатен Случаен Помощник',
      description: 'Не можете да решите? Завъртете колелото и нека то избере вместо вас. Добавете опции, завъртете и получете незабавен отговор. Безплатно, без регистрация.',
    },
    '/trivia-wheel': {
      title: 'Колело на Тривия — Безплатен Избор на Категория',
      description: 'Завъртете колелото на тривия за мигновен случаен избор на категория за викторина. Безплатна игра за вечери с въпроси, класни стаи и партита.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Създател на Колело на Съдбата — Безплатно',
      description: 'Създайте своето колело на съдбата онлайн за минута — безплатно, без регистрация. Добавете награди или категории, завъртете и споделете връзката.',
    },
    '/spin-the-bottle': {
      title: 'Завъртане на Бутилката Online — Безплатна Парти Игра',
      description: 'Безплатна онлайн игра „Завъртане на бутилката" за партита. Добавете играчи и завъртете за случаен избор. Идеална за „Истина или предизвикателство".',
    },
    '/number-wheel': {
      title: 'Колело на Числата — Безплатен Случаен Генератор',
      description: 'Безплатно колело на числата — задайте произволен диапазон (1–10, 1–100 или персонализиран). За математически игри, томболи и цифрови зарове. Без регистрация.',
    },
    '/random-team-generator': {
      title: 'Генератор на Случайни Отбори — Безплатно',
      description: 'Разделете всяка група на справедливи случайни отбори за секунди. Безплатно за учители, мениджъри и играчи. Без регистрация.',
    },
  },

  uk: {
    '/': {
      title: 'Безкоштовне Колесо Фортуни і Генератор Імен',
      description: 'Безкоштовне онлайн-колесо — додайте імена і крутіть, щоб вибрати випадкового переможця. Ідеально для класу, розіграшів, лотерей і рішень.',
    },
    '/classroom-name-picker': {
      title: 'Випадковий Вибір Учня — Безкоштовний Інструмент',
      description: 'Безкоштовний генератор випадкових імен для вчителів. Вибирайте учнів справедливо і випадково. Додайте список класу і одразу крутіть колесо.',
    },
    '/giveaway-wheel': {
      title: 'Колесо Розіграшу — Вибір Переможця Безкоштовно',
      description: 'Проведіть справедливий і прозорий розіграш з нашим безкоштовним колесом. Додайте учасників і крутіть для вибору випадкового переможця.',
    },
    '/yes-no-wheel': {
      title: 'Колесо Так чи Ні — Безкоштовне Випадкове Рішення',
      description: 'Не можете вирішити? Крутіть наше безкоштовне колесо «Так чи Ні» для миттєвої випадкової відповіді. Чудово підходить для ігор і повсякденних виборів.',
    },
    '/gallery': {
      title: 'Галерея Шаблонів — Готові Колеса Фортуни',
      description: 'Перегляньте 48 готових шаблонів колеса. Завантажте колесо одним кліком: дні, кубик, так/ні, жанри фільмів та інше.',
    },
    '/how-to': {
      title: 'Як Користуватися Колесом — Посібник',
      description: 'Дізнайтеся, як користуватися колесом TheNameWheel. Додайте імена, налаштуйте колесо, крутіть і діліться результатами.',
    },
    '/faq': {
      title: 'Часті Запитання — Колесо TheNameWheel',
      description: 'Відповіді на часті запитання про TheNameWheel. Чи справді вибір випадковий? Як це працює? Чи можна зберегти колесо?',
    },
    '/about': {
      title: 'Про TheNameWheel — Безкоштовний Вибір Імен',
      description: 'TheNameWheel — безкоштовний приватний інструмент для випадкового вибору імен, створений для вчителів, стримерів і всіх, кому потрібен справедливий вибір.',
    },
    '/contact': {
      title: 'Зв\'яжіться з Нами | TheNameWheel',
      description: 'Маєте запитання, запит функції або відгук про TheNameWheel? Надішліть нам повідомлення через нашу форму зворотного зв\'язку.',
    },
    '/classes': {
      title: 'Ваші Класи — Збережені Колеса | TheNameWheel',
      description: 'Керуйте всіма збереженими класними колесами: списки учнів, історія обертань і хто ще не був вибраний. Зберігається в браузері, дані не завантажуються.',
    },
    '/random-letter-wheel': {
      title: 'Колесо Випадкових Літер — Безкоштовний Вибір',
      description: 'Крутіть алфавітне колесо для вибору випадкової літери. А–Я, лише голосні чи приголосні. Ідеально для Scrabble, словесних ігор і шкільних занять.',
    },
    '/prize-wheel': {
      title: 'Колесо Призів — Безкоштовне Онлайн-Жеребкування',
      description: 'Створіть безкоштовне колесо призів для заходів і розіграшів. Додайте призи, крутіть для виграшу, використовуйте автовидалення для чесних розіграшів.',
    },
    '/decision-wheel': {
      title: 'Колесо Рішень — Безкоштовний Випадковий Вибір',
      description: 'Не можете вирішити? Крутіть колесо і нехай воно обере за вас. Додайте варіанти, крутіть і отримайте миттєву відповідь. Безкоштовно, без реєстрації.',
    },
    '/trivia-wheel': {
      title: 'Колесо Вікторини — Безкоштовний Вибір Категорії',
      description: 'Крутіть колесо вікторини для миттєвого вибору категорії квізу. Безкоштовна гра для вечорів запитань, уроків і вечірок. 10 категорій, повне налаштування.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Створити Колесо Фортуни — Безкоштовно',
      description: 'Створіть власне колесо фортуни онлайн за хвилину — безкоштовно, без реєстрації. Додайте призи або категорії, крутіть і поширюйте посилання.',
    },
    '/spin-the-bottle': {
      title: 'Пляшечка Онлайн — Безкоштовна Гра для Вечірки',
      description: 'Безкоштовна онлайн-гра у пляшечку для вечірок. Додайте гравців і крутіть колесо для випадкового вибору. Ідеально для правди чи дії. Без завантаження.',
    },
    '/number-wheel': {
      title: 'Колесо Чисел — Безкоштовний Генератор Чисел',
      description: 'Безкоштовне колесо чисел — задайте будь-який діапазон (1–10, 1–100 або власний). Для математичних ігор, розіграшів і цифрових кубиків. Без реєстрації.',
    },
    '/random-team-generator': {
      title: 'Генератор Випадкових Команд — Безкоштовно',
      description: 'Розділіть будь-яку групу на справедливі випадкові команди за секунди. Безкоштовно для вчителів, менеджерів і гравців. Без реєстрації.',
    },
  },

  ms: {
    '/': {
      title: 'Roda Rawak Percuma dan Pemilih Nama',
      description: 'Roda rawak percuma dalam talian — tambah nama dan putar untuk memilih pemenang secara rawak. Sesuai untuk kelas, hadiah, cabutan bertuah dan keputusan.',
    },
    '/classroom-name-picker': {
      title: 'Pemilih Nama Kelas — Alat Percuma untuk Guru',
      description: 'Alat pemilih nama rawak percuma untuk guru. Pilih pelajar secara adil dan rawak. Tambah senarai kelas anda dan putar sekarang.',
    },
    '/giveaway-wheel': {
      title: 'Roda Cabutan Bertuah — Pemilih Pemenang Percuma',
      description: 'Jalankan cabutan bertuah yang adil dan telus dengan roda percuma kami. Tambah peserta dan putar untuk memilih pemenang secara rawak.',
    },
    '/yes-no-wheel': {
      title: 'Roda Ya atau Tidak — Keputusan Rawak Percuma',
      description: 'Tidak boleh membuat keputusan? Putar roda Ya/Tidak percuma kami untuk jawapan rawak segera. Sesuai untuk permainan dan pilihan harian.',
    },
    '/gallery': {
      title: 'Galeri Templat — Roda Sedia Guna',
      description: 'Terokai 48 templat roda sedia guna. Muatkan roda dengan satu klik: hari, dadu, ya/tidak, genre filem dan lain-lain.',
    },
    '/how-to': {
      title: 'Cara Menggunakan Roda Rawak — Panduan',
      description: 'Pelajari cara menggunakan roda TheNameWheel. Tambah nama, sesuaikan roda, putar dan kongsi keputusan.',
    },
    '/faq': {
      title: 'Soalan Lazim — Roda TheNameWheel',
      description: 'Jawapan kepada soalan lazim tentang TheNameWheel. Adakah ia benar-benar rawak? Bagaimana ia berfungsi? Bolehkah saya menyimpan roda saya?',
    },
    '/about': {
      title: 'Tentang TheNameWheel — Pemilih Nama Percuma',
      description: 'TheNameWheel adalah alat pemilih nama rawak yang percuma dan peribadi, dicipta untuk guru, streamer dan sesiapa sahaja yang memerlukan pilihan yang adil.',
    },
    '/contact': {
      title: 'Hubungi Kami | TheNameWheel',
      description: 'Ada soalan, permintaan ciri atau maklum balas tentang TheNameWheel? Hantar mesej melalui borang hubungi kami.',
    },
    '/classes': {
      title: 'Kelas Anda — Roda Tersimpan | TheNameWheel',
      description: 'Urus semua roda kelas anda yang tersimpan: senarai pelajar, sejarah putaran dan siapa yang belum dipilih. Disimpan dalam pelayar anda, nama pelajar tidak meninggalkan peranti.',
    },
    '/random-letter-wheel': {
      title: 'Roda Huruf Rawak — Pemilih Abjad Percuma',
      description: 'Putar roda abjad untuk memilih huruf secara rawak. A–Z, vokal sahaja atau konsonan. Sesuai untuk Scrabble, permainan perkataan dan aktiviti kelas.',
    },
    '/prize-wheel': {
      title: 'Roda Hadiah — Cabutan Dalam Talian Percuma',
      description: 'Cipta roda hadiah percuma untuk acara dan cabutan bertuah. Tambah hadiah, putar untuk menang dan gunakan alih keluar automatik untuk cabutan berbilang hadiah.',
    },
    '/decision-wheel': {
      title: 'Roda Keputusan — Pembuat Keputusan Rawak Percuma',
      description: 'Tidak boleh membuat keputusan? Putar roda dan biarkan ia memilih untuk anda. Tambah pilihan, putar dan dapatkan jawapan segera. Percuma, tanpa pendaftaran.',
    },
    '/trivia-wheel': {
      title: 'Roda Trivia — Pemilih Kategori Kuiz Percuma',
      description: 'Putar roda trivia untuk memilih kategori kuiz secara rawak dengan serta-merta. Permainan percuma untuk malam kuiz, kelas dan pesta. 10 kategori, sepenuhnya boleh disesuaikan.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Pembuat Roda Nasib — Percuma Dalam Talian',
      description: 'Cipta roda nasib sendiri dalam talian — percuma, tanpa pendaftaran. Tambah hadiah atau kategori, kemudian putar, kongsi pautan atau benamkan roda.',
    },
    '/spin-the-bottle': {
      title: 'Pusing Botol Dalam Talian — Aplikasi Pesta Percuma',
      description: 'Aplikasi pusing botol percuma untuk majlis pesta. Tambah nama pemain dan putar roda untuk pilihan rawak. Sesuai untuk benar atau cabaran. Tanpa muat turun.',
    },
    '/number-wheel': {
      title: 'Roda Nombor — Penjana Nombor Rawak Percuma',
      description: 'Roda nombor percuma — tetapkan sebarang julat (1–10, 1–100 atau tersuai). Untuk permainan matematik, cabutan bertuah dan dadu digital. Tanpa pendaftaran.',
    },
    '/random-team-generator': {
      title: 'Penjana Pasukan Rawak — Alat Percuma',
      description: 'Bahagikan sebarang kumpulan kepada pasukan rawak yang adil dalam beberapa saat. Alat percuma untuk guru, pengurus dan pemain. Tanpa pendaftaran.',
    },
  },

  tl: {
    '/': {
      title: 'Libreng Random na Gulong at Pumili ng Pangalan',
      description: 'Libreng random na gulong online — magdagdag ng pangalan at umikot para pumili ng random na panalo. Perpekto para sa klase, giveaway, raffle, at desisyon.',
    },
    '/classroom-name-picker': {
      title: 'Pumili ng Pangalan sa Klase para sa Guro',
      description: 'Libreng random na pumili ng pangalan para sa mga guro. Pumili ng estudyante nang patas at random. Idagdag ang listahan ng klase at umikot na agad.',
    },
    '/giveaway-wheel': {
      title: 'Giveaway na Gulong — Libreng Pumili ng Panalo',
      description: 'Magsagawa ng patas at transparent na giveaway gamit ang aming libreng gulong. Idagdag ang mga kalahok at umikot para pumili ng random na mananalo.',
    },
    '/yes-no-wheel': {
      title: 'Oo o Hindi na Gulong — Libreng Random na Desisyon',
      description: 'Hindi makapagdesisyon? Ikutin ang aming libreng Oo/Hindi na gulong para sa agarang random na sagot. Perpekto para sa mga laro at pang-araw-araw na pagpili.',
    },
    '/gallery': {
      title: 'Galerya ng Template — Mga Gawa Nang Gulong',
      description: 'I-explore ang 48 na gawa nang template ng gulong. I-load ang gulong sa isang click: mga araw, dais, oo/hindi, mga genre ng pelikula at marami pa.',
    },
    '/how-to': {
      title: 'Paano Gamitin ang Random na Gulong — Gabay',
      description: 'Alamin kung paano gamitin ang gulong ng TheNameWheel. Magdagdag ng pangalan, i-customize ang gulong, umikot at ibahagi ang mga resulta.',
    },
    '/faq': {
      title: 'Mga Madalas na Tanong — Gulong ng TheNameWheel',
      description: 'Mga sagot sa mga karaniwang tanong tungkol sa TheNameWheel. Tunay bang random? Paano ito gumagana? Maaari ko bang i-save ang aking gulong?',
    },
    '/about': {
      title: 'Tungkol sa TheNameWheel — Libreng Pumili ng Pangalan',
      description: 'Ang TheNameWheel ay isang libre at pribadong tool para sa random na pagpili ng pangalan, ginawa para sa mga guro, streamer at sinumang nangangailangan ng patas na pagpili.',
    },
    '/contact': {
      title: 'Makipag-ugnayan | TheNameWheel',
      description: 'May tanong, kahilingan sa feature, o feedback tungkol sa TheNameWheel? Magpadala ng mensahe sa pamamagitan ng aming contact form.',
    },
    '/classes': {
      title: 'Iyong mga Klase — Naka-save na Gulong | TheNameWheel',
      description: 'Pamahalaan ang lahat ng iyong naka-save na class wheel: listahan ng mag-aaral, kasaysayan ng pag-ikot, at sino pa ang hindi napili. Nakatago sa browser, hindi lumalabas ang mga pangalan.',
    },
    '/random-letter-wheel': {
      title: 'Random Letter Wheel — Libreng Alphabet Selector',
      description: 'Ikutin ang alphabet wheel para pumili ng random na letra. A–Z, katinig lamang o patinig lamang. Para sa word games, Scrabble, at mga aktibidad sa klase.',
    },
    '/prize-wheel': {
      title: 'Prize Wheel — Libreng Online na Palabunutan',
      description: 'Gumawa ng libreng prize wheel para sa mga event at giveaway. Magdagdag ng mga premyo, ikutin para manalo, at gamitin ang auto-remove para sa patas na multi-prize draw.',
    },
    '/decision-wheel': {
      title: 'Decision Wheel — Libreng Random Decision Maker',
      description: 'Hindi makapagdesisyon? Ikutin ang decision wheel at hayaan itong pumili para sa iyo. Magdagdag ng mga opsyon, ikutin, at makakuha ng agarang sagot. Libre, walang signup.',
    },
    '/trivia-wheel': {
      title: 'Trivia Wheel — Libreng Quiz Category Selector',
      description: 'Ikutin ang trivia wheel para pumili ng random na quiz category kaagad. Libreng laro para sa quiz nights, klase, at party. 10 kategorya, ganap na mako-customize.',
    },
    '/wheel-of-fortune-maker': {
      title: 'Wheel of Fortune Maker — Libreng Online',
      description: 'Gumawa ng sarili mong wheel of fortune online sa loob ng isang minuto — libre, walang signup. Magdagdag ng mga premyo o kategorya, ikutin at ibahagi ang link.',
    },
    '/spin-the-bottle': {
      title: 'Spin the Bottle Online — Libreng Party Game App',
      description: 'Libreng spin the bottle app para sa mga party. Idagdag ang mga pangalan ng manlalaro at ikutin ang gulong para pumili ng random. Para sa truth or dare. Walang download.',
    },
    '/number-wheel': {
      title: 'Number Wheel — Libreng Random Number Generator',
      description: 'Libreng number wheel — itakda ang anumang hanay (1–10, 1–100 o custom). Para sa math games, palabunutan, at digital na dice. Walang signup.',
    },
    '/random-team-generator': {
      title: 'Random Team Generator — Libreng Hatiin ang Grupo',
      description: 'Hatiin ang anumang grupo sa mga patas na random na koponan sa ilang segundo. Libreng tool para sa mga guro, manager, at manlalaro. Walang signup.',
    },
  },
};


/** Languages with at least one translated route. */
export const SEO_LOCALE_LANGS = Object.keys(SEO_LOCALES);

/** Translated copy for a route, or null to fall back to the English map. */
export function localisedSEO(lang: string, route: string): LocalisedSEO | null {
  return SEO_LOCALES[lang]?.[route] ?? null;
}
